import React, {
  useEffect,
  useMemo,
  forwardRef,
  useLayoutEffect,
  useImperativeHandle,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Link } from "react-router-dom";
import { AppSettings } from "./../../config/app-settings.js";
import { Modal, Button } from "bootstrap";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import Select from "react-select";
import { Circles } from "react-loader-spinner";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
} from "recharts";
import autoTable from "jspdf-autotable";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import SignaturePad from "signature_pad";
import { jsPDF } from "jspdf";

const llantasData = [
  { year: "2022", siniestralidad: 66.2, costo: 3251.74 },
  { year: "2023", siniestralidad: 49.7, costo: 3319.51 },
  { year: "2024", siniestralidad: 57.5, costo: 3342.78 },
  { year: "2025", siniestralidad: 52.2, costo: 3170.82 },
];

const mecanicaData = [
  { year: "2020", siniestralidad: 22.8, costo: 16273.15 },
  { year: "2021", siniestralidad: 22.9, costo: 22332.09 },
  { year: "2022", siniestralidad: 33.1, costo: 27656.05 },
  { year: "2023", siniestralidad: 38.1, costo: 38517.54 },
  { year: "2024", siniestralidad: 39.5, costo: 38751.5 },
];

const reparacionesData = [
  { year: "2022", costo: 34637, color: "#20c997" },
  { year: "2023", costo: 37357, color: "#0d6efd" },
  { year: "2024", costo: 38575, color: "#002752" }
];

const siniestralidadData = [
  { name: "2022-2023", value: 42.99, color: "#007bff" },
  { name: "2023-2024", value: 48.24, color: "#dc3545" }
];

const CanvasOverlay = forwardRef(({ containerRef }, ref) => {
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const ctxRef = useRef(null);

  useImperativeHandle(ref, () => ({
    clearCanvas: () => {
      const ctx = ctxRef.current;
      if (ctx && canvasRef.current) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    },
  }));

  useEffect(() => {
    if (!containerRef?.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    setDimensions({ width: rect.width, height: rect.height });

    const observer = new ResizeObserver((entries) => {
      if (!entries?.[0]?.target) return;
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [containerRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dimensions.width || !dimensions.height) return;

    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "blue";
    ctx.lineWidth = 1.5;
    ctxRef.current = ctx;

    let isDrawing = false;

    // Utilidad para obtener coordenadas desde evento de mouse o touch
    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      if (e.touches && e.touches.length > 0) {
        return {
          x: e.touches[0].clientX - rect.left,
          y: e.touches[0].clientY - rect.top,
        };
      } else {
        return {
          x: e.offsetX,
          y: e.offsetY,
        };
      }
    };

    const startDraw = (e) => {
      e.preventDefault();
      const { x, y } = getPos(e);
      isDrawing = true;
      ctx.beginPath();
      ctx.moveTo(x, y);
    };

    const draw = (e) => {
      if (!isDrawing) return;
      e.preventDefault();
      const { x, y } = getPos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const stopDraw = () => {
      isDrawing = false;
    };

    // Mouse events
    canvas.addEventListener("mousedown", startDraw);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("mouseup", stopDraw);
    canvas.addEventListener("mouseout", stopDraw);

    // Touch events
    canvas.addEventListener("touchstart", startDraw, { passive: false });
    canvas.addEventListener("touchmove", draw, { passive: false });
    canvas.addEventListener("touchend", stopDraw);

    return () => {
      canvas.removeEventListener("mousedown", startDraw);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("mouseup", stopDraw);
      canvas.removeEventListener("mouseout", stopDraw);

      canvas.removeEventListener("touchstart", startDraw);
      canvas.removeEventListener("touchmove", draw);
      canvas.removeEventListener("touchend", stopDraw);
    };
  }, [dimensions]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        border: "2px dashed rgba(205, 205, 205, 0.56)",
        top: 0,
        left: 0,
        zIndex: 10,
        pointerEvents: "auto",
        touchAction: "none", //para evitar scroll
      }}
    />
  );
});

const ZoomImage = ({
  src,
  alt = "",
  style = {},
  zoom = 2,
  maxHeight = 480,
  onError,
}) => {
  const imgRef = useRef(null);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0, show: false });
  const [imgSrc, setImgSrc] = useState(src);

  const handleMouseMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setLensPos({ x, y, show: true });
  };

  const handleMouseLeave = () => {
    setLensPos((lensPos) => ({ ...lensPos, show: false }));
  };

  const handleImgError = (e) => {
    setImgSrc("/assets/img/product/noimage.png");
    if (onError) onError(e);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxHeight,
        overflow: "hidden",
        borderRadius: style.borderRadius || 0,
        cursor: "zoom-in",
        display: "flex", // centrado horizontal
        justifyContent: "center", // centrado horizontal
        ...style,
      }}
    >
      <img
        src={imgSrc}
        alt={alt}
        ref={imgRef}
        onError={handleImgError}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          width: "70%", // imagen más pequeña
          height: "auto",
          maxHeight,
          objectFit: "contain",
          transition: "transform 0.3s ease",
          display: "block",
          borderRadius: "15px",
        }}
      />
      {lensPos.show && (
        <div
          style={{
            position: "absolute",
            top: lensPos.y - 75,
            left: lensPos.x - 75,
            width: 150,
            height: 150,
            border: "2px solidrgb(0, 42, 255)",
            backgroundImage: `url(${imgSrc})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${imgRef.current ? imgRef.current.width * zoom : 0
              }px ${imgRef.current ? imgRef.current.height * zoom : 0}px`,
            backgroundPositionX: -lensPos.x * zoom + 75,
            backgroundPositionY: -lensPos.y * zoom + 75,
            pointerEvents: "none",
            borderRadius: "50%",
            boxShadow: "0 0 8pxrgb(0, 60, 255)",
            zIndex: 9999,
          }}
        />
      )}
    </div>
  );
};

const apiUrl = "https://api.checkmark.mx/cliente/ventas/financiamiento";
const apiPDF = "https://api.checkmark.mx/enviar/pdf"; //envio pdf
const apiUrlInventario = "https://api.checkmark.mx/financiamiento/vehiculos";
const accessToken =
  "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

const customStyles = {
  control: (base) => ({
    ...base,
    borderColor: "transparent",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    borderRadius: "0",
    color: "white",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#fff",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    color: "black", // Text color for options
    backgroundColor: state.isFocused ? "transparent" : "transparent",
    "&:active": {
      backgroundColor: "transparent",
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: "white", // Color of the selected value
  }),
  input: (base) => ({
    ...base,
    color: "white", // Color of the input text
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "white", // Change the color of the dropdown indicator to white
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "white", // Change the color of the clear indicator to white
  }),
};

// Mapeo de agencias a marcas (para nuevos) y ag_id (para seminuevos)
const agenciaInfo = {
  "Seminuevos Gocar Azcapotzalco": {
    marca: "Renault",
    ag_id: "ag0028_5c33da0cc1c4d",
  },
  "Renault San Ángel": { marca: "Renault", ag_id: "ag0532_5598bb1c4042b" },
  "Honda Santa Fe": { marca: "Honda", ag_id: "ag0532_5598bb1c488c9" },
  "Nissan Aragón": { marca: "Nissan", ag_id: "ag0532_5598bb1c503e6" },
  "Volkswagen León Motors": {
    marca: "Volkswagen",
    ag_id: "ag0532_5598bb1c51d78",
  },
  "Seat Seminuevos Cerro Gordo": {
    marca: "Seat",
    ag_id: "ag0532_5598bb1c56605",
  },
  "Nissan Santa Clara": { marca: "Nissan", ag_id: "ag0532_5598bb1c86d5a" },
  "Renault Universidad": { marca: "Renault", ag_id: "ag0533_5598bb1d621ab" },
  "Gocar Seminuevos León Irapuato": { ag_id: "ag0931_6500d38be2bc6" },
  "Gocar Seminuevos León": { ag_id: "ag1035_6500d3cb15aed" },
  "MG Ecatepec": { marca: "MG", ag_id: "ag1150_5b44ccb6362b8" },
  "MG Toluca": { marca: "MG", ag_id: "ag2327_620bfdaf30656" },
  "Chirey Ecatepec": { marca: "Chirey", ag_id: "ag3307_631f6d5397531" },
  "MG Metepec": { marca: "MG", ag_id: "ag3636_61d5d72451639" },
  "Renault Ajusco": { marca: "Renault", ag_id: "ag3912_5980f500b41b4" },
  "Honda Los Cabos": { marca: "Honda", ag_id: "ag5423_5a8736ef2eeb7" },
  "Honda Metepec": { marca: "Honda", ag_id: "ag5457_5e20f7d1c1032" },
  "Honda Tollocan": { marca: "Honda", ag_id: "ag5650_5e20f842e21a6" },
  "Nissan Texcoco": { marca: "Nissan", ag_id: "ag5713_5c33d949e445e" },
  "Renault San Ajusco - Nuevos": {
    marca: "Renault",
    ag_id: "ag5822_5bacfe1e8a7aa",
  },
  "Nissan Tecámac": { marca: "Nissan", ag_id: "ag5912_5c33d9c0c10df" },
};

function generateFolio() {
  return Math.random().toString(36).substring(2, 15).toUpperCase();
}

const formatterPeso = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 0,
});

// Función para formatear moneda
const formatCurrency = (value) => {
  return isNaN(Number(value)) ? "$0" : `$${Number(value).toLocaleString()}`;
};

const renderBarLabel = ({ x, y, width, height, value }) => {
  const cx = x + width / 2;
  const cy = y + height / 2;

  return (
    <text
      x={cx}
      y={cy}
      fill="#fff"
      fontSize={11}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {formatCurrency(value)}
    </text>
  );
};

const renderBarLabel2 = ({ x, y, width, value }) => {
  const cx = x + width / 2;
  const cy = y - 8;

  //variable modo claro/oscuro
  const labelColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--label-color')
    .trim() || '#000';

  return (
    <text
      x={cx}
      y={cy}
      fill={labelColor}
      fontSize={11}
      textAnchor="middle"
      dominantBaseline="central"
    >
      {formatCurrency(value)}
    </text>
  );
};


const GraficasEstadisticas = () => {
  const siniestralidadData = [
    { name: "Sin siniestro", value: 27.4 },
    { name: "Con siniestro", value: 72.6 },
  ];

  const reparacionData = [
    { name: "Choque menor", costo: 7500 },
    { name: "Lesión menor", costo: 25000 },
    { name: "Lesión grave", costo: 350000 },
    { name: "Catastrofe", costo: 1500000 },
  ];

  const roboAutosData = [
    { name: "Total", vehiculos: 136993 },
    { name: "Asegurados robados", vehiculos: 61222 },
    { name: "Sin seguro", vehiculos: 75871 },
    { name: "Robo con violencia", vehiculos: 78087 },
  ];

  const distribucionSiniestrosData = [
    { name: "Robos", value: 20 },
    { name: "Responsabilidad civil", value: 30 },
    { name: "Daños materiales", value: 50 },
  ];

  return (
    <div className="container-fluid">
      <br></br>
      <h6 className="text-center text-light mb-3">
        🚘 Datos clave sobre riesgos y costos en el sector asegurador.
      </h6>
      <br></br>
      <div className="row">
        {/* Siniestralidad */}
        <div className="col-md-6 mb-4">
          <div
            className="p-3 rounded modal-grafic"
            style={{ backgroundColor: "#1f2e35" }}
          >
            <h6 className="text-warning text-center mb-2">
              📊 Siniestralidad en el sector asegurador
            </h6>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={siniestralidadData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ value }) => `${value}%`}
                >
                  <Cell fill="#66bb6a" />
                  <Cell fill="#f44336" />
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
            <p className="text-light text-center mt-2 text-base">
              El 72.6% de los autos asegurados presentan algún siniestro.
            </p>
          </div>
        </div>

        {/* Costos de reparación */}
        <div className="col-md-6 mb-4">
          <div
            className="p-3 rounded modal-grafic"
            style={{ backgroundColor: "#1f2e35" }}
          >
            <h6 className="text-warning text-center mb-2">
              💰 Costos de reparación por accidente
            </h6>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={reparacionData} margin={{ top: 20, left: 15 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="var(--axis-stroke)" />
                <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                <Tooltip formatter={(value) => value.toLocaleString()} />
                <Legend />
                <Bar dataKey="costo" fill="#f44336" label={renderBarLabel2} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-light text-center mt-2 text-base">
              Los costos de reparación pueden ser extremadamente elevados sin
              seguro.
            </p>
          </div>
        </div>

        {/* Robo de autos */}
        <div className="col-md-6 mb-4">
          <div
            className="p-3 rounded modal-grafic"
            style={{ backgroundColor: "#1f2e35" }}
          >
            <h6 className="text-warning text-center mb-2">
              🚗 Robo de autos en México
            </h6>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={roboAutosData} margin={{ top: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" stroke="var(--axis-stroke)" />
                <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                <Tooltip formatter={(value) => value.toLocaleString()} />
                <Legend />
                <Bar dataKey="vehiculos" fill="#ff9800" label={renderBarLabel2} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-light text-center mt-2 text-base">
              Casi 137,000 autos fueron robados en 2023, más de la mitad sin
              seguro.
            </p>
          </div>
        </div>

        {/* Distribución de tipos de siniestro */}
        <div className="col-md-6 mb-4">
          <div
            className="p-3 rounded modal-grafic"
            style={{ backgroundColor: "#1f2e35" }}
          >
            <h6 className="text-warning text-center mb-2 ">
              📌 Tipos de siniestros más comunes
            </h6>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={distribucionSiniestrosData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  <Cell fill="#ffca28" />
                  <Cell fill="#ab47bc" />
                  <Cell fill="#26c6da" />
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
              </PieChart>
            </ResponsiveContainer>
            <p className="text-light text-center mt-2 text-base">
              Los daños materiales y responsabilidad civil representan el 80% de
              los siniestros.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

function PosCustomerOrder({ order, getSubTotalPrice }) {
  const [folioVenta, setFolioVenta] = useState("");
  const [vehicleData, setVehicleData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);
  const [agencias, setAgencias] = useState([]);
  const [selectedAgencia, setSelectedAgencia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [precioBaseVehiculo, setPrecioBaseVehiculo] = useState(0);
  const [garantia_extendida, setGarantiaExtendida] = useState(""); //new para modal
  const [mostrarModalInfo, setMostrarModalInfo] = useState(false); //new
  const [isSubmitting, setIsSubmitting] = useState(false); //new
  const [selectedPlan, setSelectedPlan] = useState(null); // new
  const [totalAccesorio, setTotalAccesorio] = useState(0); //total precio plan seleccionado
  const [plans, setPlans] = useState([]); // Estado para los accesorios 
  const [selectedMailData, setSelectedMailData] = useState(null); //new
  const [pago_contado, setContado] = useState(false); //new
  const [accessoryModalVisible, setAccessoryModalVisible] = useState(false);
  const [accessoryModalData, setAccessoryModalData] = useState(null); //modal para los accesorios
  const [mailData, setMailData] = useState([]);
  const planesRef = useRef(); //nueva
  const [planSeleccionado, setPlanSeleccionado] = useState(null); //plan seleccionado final
  const [archivos, setArchivos] = useState([]);     //aquí guardaremos los PDF
  const [readyToSend, setReadyToSend] = useState(false);
  const [tipoEnvio, setTipoEnvio] = useState("rechazo"); // tipo de envio
  const rechazoSeleccionado = garantia_extendida === "Si" || garantia_extendida === "No";
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    correo_electronico: "",
    acepta_aviso_privacidad: false,
    depende_auto: "",
    condiciones_calle: "",
    perdio_llaves: "",
    tipo_auto: "",
    preocupacion_fallas: "",
    preocupacion_robo: "",
    ingreso_mensual: "",
    edad: "",
    enganche: "",
    historial_crediticio: "",
    tipo_trabajo: "",
    asesor: "",
    user_id: "",
    venta_id: "",
  });

  const [filters, setFilters] = useState({
    activo: "",
  });

  function resetVenta() {
    sessionStorage.clear();
    setFolioVenta("");
    setVehicleData(null);
    setShowModal(false);
    setIsDisabled(false);
    setAgencias([]);
    setSelectedAgencia(null);
    setLoading(false);
    setInitialLoad(true);
    setPrecioBaseVehiculo(0);
    setGarantiaExtendida("");
    setMostrarModalInfo(false);
    setIsSubmitting(false);
    setSelectedPlan(null);
    setTotalAccesorio(0);
    setPlans([]);
    setSelectedMailData(null);
    setContado(false);
    setAccessoryModalVisible(false);
    setAccessoryModalData(null);
    setMailData([]);
    setPlanSeleccionado(null);
    setArchivos([]);
    setReadyToSend(false);
    setTipoEnvio("rechazo");
    setFormData({
      nombre: "",
      telefono: "",
      correo_electronico: "",
      acepta_aviso_privacidad: false,
      depende_auto: "",
      condiciones_calle: "",
      perdio_llaves: "",
      tipo_auto: "",
      preocupacion_fallas: "",
      preocupacion_robo: "",
      ingreso_mensual: "",
      edad: "",
      enganche: "",
      historial_crediticio: "",
      tipo_trabajo: "",
      asesor: "",
      user_id: "",
      venta_id: "",
    });
    setFilters({ activo: "" });
    setAccesoriosModalValue("0");
    setFinanciamientoModalValue("0");
    setMostrarVideoIntro(false);
    setNombreCliente("");
    setUserModules(JSON.parse(localStorage.getItem("modulos")) || []);
    setPosMobileSidebarToggled(false);
    setCategoryType("all");
    setTableData([]);
    sessionStorage.removeItem("orderCart");
    setOrderData([]);
    setOrderHistoryData([]);
    setSelectedBrand(null);
    setProductModal(undefined);
    setModalData(undefined);
    setModalQuantity(undefined);
    setModalSelectedSize(undefined);
    setModalSelectedAddon([]);
    setSelectedCar(null);
    setMostrarModalAccesorios(false);
    setAccesoriosGarantias({ accesorios: [], garantias: [] });
    setCanvasActivo(false);
    setBusqueda("");
    setsearchVehicle("");
    setIsActive(true);
    setOpenFicha(false);
    window.location.reload();
  }

  const agregarArchivo = (nuevo) => { //agregar los archivos al array
    console.log("Agregando archivo(s):", nuevo);

    setArchivos((prev) => {
      const nuevosArchivos = (nuevo instanceof FileList || Array.isArray(nuevo))
        ? [...nuevo]
        : [nuevo];

      console.log("Archivos actuales antes de agregar:", prev);
      console.log("Archivos después de agregar:", [...prev, ...nuevosArchivos]);

      return [...prev, ...nuevosArchivos];
    });
  };

  const accessToken =
    "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3"; // El token de acceso
  const apiUrlConsulta = "https://api.checkmark.mx/ventas/telefono/consultar"; // La URL de la API
  const apiUrlUpdate =
    "https://api.checkmark.mx/actualizar/estado/financiamiento"; //actualizar los estados

  const generarDocumentos = async () => {
    //generar los documentos
    if (!selectedMailData) return;

    try {
      // Cierra el modal inicial al comenzar
      handleCloseModalFinanciamiento();

      if (selectedMailData?.depende_auto === "Si") { //aqui tambien cambiar MailData
        generarPDFcontado();
      } else {
        generarPDF();
      }

      if (garantia_extendida === "Si") {
        await generarPDFAceptacion();
        await generarPDFrenuncia();
      }

      const result = await Swal.fire({
        icon: "success",
        title: "Listo",
        text: "Sus documentos fueron enviados a su correo con éxito.",
        confirmButtonText: "Aceptar",
      });

      // Cuando el usuario presione "Aceptar"
      if (result.isConfirmed) {
        setIsActive(false);
        // actualizamos en API para cambiar financiamiento_modal a 0
        await fetch(apiUrlUpdate, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_token: accessToken,
            venta_id: selectedMailData.id,
            financiamiento_modal: "0",
          }),
        });

        setFinanciamientoModalValue("0");
        //resetVenta();
      }
    } catch (error) {
      console.error("Error generando documentos:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al generar los documentos.",
        confirmButtonText: "Aceptar",
      });
    }
  };

  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === "selectedMailData" && event.newValue) {
        try {
          const parsedData = JSON.parse(event.newValue);
          setSelectedMailData(parsedData); // <- Esto activa tu useEffect existente
        } catch (error) {
          console.error("Error al parsear selectedMailData:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);
    return () => window.removeEventListener("storage", handleStorageEvent);
  }, []);

  useEffect(() => {
    try {
      // Parsear planes_financiamiento
      const planes = JSON.parse(
        selectedMailData?.planes_financiamiento || "[]"
      );
      const planSeleccionado = planes.find((plan) => plan.isSelected === true);

      // Obtener planes_seleccionados y parsearlo
      const accesoriosRaw = planSeleccionado?.accessories || [];

      try {
        if (typeof accesoriosRaw === "string") {
          accesoriosRaw = JSON.parse(accesoriosRaw);

          if (typeof accesoriosRaw === "string") {
            accesoriosRaw = JSON.parse(accesoriosRaw);
          }
        }
      } catch (e) {
        console.error("Error parseando planes_seleccionados:", e);
        accesoriosRaw = [];
      }

      // Convertir los precios a número
      const accesoriosConvertidos = (
        Array.isArray(accesoriosRaw) ? accesoriosRaw : []
      ).map((acc) => ({
        ...acc,
        price: Number(
          acc.price?.toString().replace("$", "").replace(",", "").trim()
        ),
      }));

      // Asignar plan y accesorios
      if (planSeleccionado) {
        setSelectedPlan(planSeleccionado);
        setTotalAccesorio(planSeleccionado.totalPrice || 0);
      } else {
        setSelectedPlan(null);
        setTotalAccesorio(0);
      }

      console.log("planes_seleccionados convertidos:", accesoriosConvertidos);
      setPlans(accesoriosConvertidos);
    } catch (error) {
      console.error("Error general en useEffect:", error);
      setSelectedPlan(null);
      setTotalAccesorio(0);
      setPlans([]);
    }
  }, [selectedMailData]);

  //******************************** GENERA PDF COTIZACIÓN FINANCIAMIENTO ***********************************************************/
  const generarPDF = () => {
    setTipoEnvio("cotizacion");
    const dataToSend = {
      ...formData,
      nombre: selectedMailData?.nombre || "",
      telefono: selectedMailData?.telefono || "",
      correo_electronico: selectedMailData?.correo_electronico || "",
      edad: selectedMailData?.edad || "",
      ingreso_mensual: selectedMailData?.ingreso_mensual || "",
      marca_vehiculo: selectedMailData?.marca_vehiculo || "",
      modelo_vehiculo: selectedMailData?.modelo_vehiculo || "",
      version_vehiculo: selectedMailData?.version_vehiculo || "",
      meses_financiamiento: selectedMailData?.meses_financiamiento || "",
      porcentaje_enganche: selectedMailData?.porcentaje_enganche || "",
      mensualidad_estimacion: selectedMailData?.mensualidad_estimacion || "",
      agencia: selectedMailData?.agencia || "",
      ag_id: selectedMailData?.ag_id || "",
      precio_total: selectedMailData?.precio_vehiculo || "",
      enganche: selectedMailData?.enganche || "",
      agencyName: selectedMailData?.agencia || "",
      total_accesorios: totalAccesorio,
      carImageURL: selectedMailData.url_img
    };

    const doc = new jsPDF();
    const fecha_creacion = new Date().toLocaleDateString();
    let band = 0; //bandera de control de paginas

    doc.setFontSize(8);
    doc.text(`${folioVenta}`, 10, 10);
    doc.text(`${fecha_creacion}`, 35, 10);

    // Agregar logo y título
    doc.setFontSize(18);
    doc.text("Cotización de Financiamiento", 105, 20, { align: "center" });

    const logoURL = "/assets/img/product/gocar_negro.png";
    //doc.addImage(dataToSend.carImageURL, "PNG", 17, 40, 75, 50); // Imagen del auto en el lado izquierdo
    doc.addImage(logoURL, "PNG", 159, 10, 40, 10.6);
    doc.setFont("helvetica", "bold"); // Cambiar a negritas
    doc.setFontSize(12);
    doc.text(selectedMailData.marca_vehiculo, 45, 85); // Marca
    doc.setFont("helvetica", "normal"); // Modelo
    doc.setFontSize(10);
    doc.text(selectedMailData.modelo_vehiculo, 47, 89);
    doc.setFontSize(12);
    // Mensualidad estimada:
    doc.setTextColor(255, 0, 0); // Color rojo
    doc.setFont("helvetica", "bold");
    doc.text("Mensualidad:", 126, 55);
    doc.text("Plazo:", 126, 64);
    doc.setTextColor(0, 0, 0); // Color negro
    doc.setFont("helvetica", "normal"); // Texto normal
    doc.text(
      Number(dataToSend.mensualidad_estimacion).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
      }),
      155,
      55
    );
    doc.text(" " + dataToSend.meses_financiamiento + " meses", 140, 64); // Escribir el resto del texto
    doc.setLineWidth(2);
    doc.setDrawColor(255, 0, 0);
    doc.line(10, 25, 200, 25);
    doc.setLineWidth(1.5);
    doc.setDrawColor(0, 0, 0);
    doc.line(10, 26, 200, 26);

    // Datos del Vehículo
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255); // Texto en blanco
    doc.setFillColor(255, 0, 0); // Fondo rojo
    doc.rect(10, 100, 91, 7, "F"); // Rectángulo para encabezado
    doc.text("Datos del Vehículo", 55, 105, { align: "center" });
    //doc.setFontSize(11);
    const datosVehiculo = [
      ["Marca:", dataToSend.marca_vehiculo],
      ["Modelo:", dataToSend.modelo_vehiculo],
      ["Versión:", dataToSend.version_vehiculo],
      [
        "Precio Total:",
        Number(dataToSend.precio_total).toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        }),
      ],
      ["Agencia:", dataToSend.agencia],
    ];
    autoTable(doc, {
      startY: 106,
      margin: { left: 10 },
      head: [["Dato", "Descripción"]],
      body: datosVehiculo,
      theme: "plain",
      styles: { fontSize: 8.5 },
      headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 35 }, // Ancho de la columna "Campo"
        1: { cellWidth: 56 }, // Ancho de la columna "Valor"
      },
    });

    // Datos del Cliente
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255); // Texto en blanco
    doc.setFillColor(255, 0, 0); // Fondo rojo
    doc.rect(110, 100, 91, 7, "F"); // Rectángulo para encabezado
    doc.text("Datos del Cliente", 153, 105, { align: "center" });
    // doc.setFontSize(8);
    const datosCliente = [
      ["Nombre:", dataToSend.nombre],
      ["Teléfono:", dataToSend.telefono],
      ["Correo:", dataToSend.correo_electronico],
      ["Edad:", dataToSend.edad + " años"],
      [
        "Ingreso mensual:",
        Number(dataToSend.ingreso_mensual).toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        }),
      ],
    ];
    autoTable(doc, {
      startY: 106,
      margin: { left: 110 },
      head: [["Dato", "Descripción"]],
      body: datosCliente,
      theme: "plain",
      styles: { fontSize: 8.5 },
      headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 35 }, // Ancho de la columna "Campo"
        1: { cellWidth: 56 }, // Ancho de la columna "Valor"
      },
    });

    // Condiciones de Financiamiento
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255); // Texto en blanco
    doc.setFillColor(255, 0, 0); // Fondo rojo
    doc.rect(10, 153, 91, 7, "F"); // Rectángulo para encabezado
    doc.text("Condiciones de Financiamiento", 57, 158, { align: "center" });
    //doc.setFontSize(11);
    console.log("Enganche recibido:", dataToSend.enganche);
    const datosFinanciamiento = [
      ["Meses de financiamiento:", dataToSend.meses_financiamiento + " meses"],
      [
        "Enganche:",
        Number(dataToSend.enganche).toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        }),
      ],
      ["Porcentaje de enganche:", `${dataToSend.porcentaje_enganche}%`],
      [
        "Mensualidad estimada:",
        Number(dataToSend.mensualidad_estimacion).toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        }),
      ],
    ];
    autoTable(doc, {
      startY: 160,
      margin: { left: 10 },
      head: [["Dato", "Descripción"]],
      body: datosFinanciamiento,
      theme: "plain",
      styles: { fontSize: 8.5 },
      headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 40 }, // Ancho de la columna "Campo"
        1: { cellWidth: 51 }, // Ancho de la columna "Valor"
      },
    });

    const yPlan = doc.lastAutoTable.finalY + 10;
    const accesorios = plans || [];
    const numAccesorios = accesorios.length;
    let accesoriosSeleccionadosY;

    if (numAccesorios >= 17) {
      let col1 = accesorios.slice(0, 16); //17 accesorios
      let col2 = accesorios.slice(17); // El resto de los accesorios

      // Procesamos las columnas
      const datosCol1 = col1.map((acc) => [
        acc.title,
        "Disponible",
        acc.price.toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        }),
      ]);

      const datosCol2 = col2.map((acc) => [
        acc.title,
        "Disponible",
        acc.price.toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        }),
      ]);

      // Encabezados manuales
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(255, 0, 0);
      doc.rect(10, 203, 91, 7, "F");
      doc.text("Accesorios Seleccionados", 58, 208, { align: "center" });

      doc.setTextColor(255, 255, 255);
      doc.setFillColor(255, 0, 0);
      doc.rect(110, 153, 91, 7, "F");
      doc.text("Accesorios Seleccionados", 155, 158, { align: "center" });

      // Tabla 1 columna derecha
      autoTable(doc, {
        startY: 160,
        margin: { left: 110 },
        head: [["Accesorio", "Estatus", "Precio"]],
        body: datosCol1,
        theme: "plain",
        styles: { fontSize: 8.5 },
        headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
        columnStyles: {
          0: { cellWidth: 43 },
          1: { cellWidth: 24 },
          2: { cellWidth: 24 },
        },
      });

      // Si hay segunda columna, la agregamos
      if (col2.length > 0) {
        // Tabla 2 columna izquierda
        autoTable(doc, {
          startY: 209,
          margin: { left: 10 },
          head: [["Accesorio", "Estatus", "Precio"]],
          body: datosCol2,
          theme: "plain",
          styles: { fontSize: 8.5 },
          headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
          columnStyles: {
            0: { cellWidth: 43 },
            1: { cellWidth: 24 },
            2: { cellWidth: 24 },
          },
        });
      }
    } else {
      // Datos ACCESORIOS
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255); // Texto en blanco
      doc.setFillColor(255, 0, 0);
      doc.rect(110, 153, 91, 7, "F"); // Rectángulo para encabezado de accesorios
      doc.text("Accesorios Seleccionados", 150, 158, { align: "center" });

      const accesorios = plans || [];
      console.log("LOS accesorios_seleccionados son:", accesorios);
      const datosAccesorios = accesorios.map((acc) => [
        acc.title,
        "Disponible",
        acc.price.toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        }),
      ]);

      autoTable(doc, {
        startY: 160, // Justo debajo del encabezado
        margin: { left: 110 },
        head: [["Accesorio", "Estatus", "Precio"]],
        body: datosAccesorios,
        theme: "plain", // Para un diseño visualmente claro
        styles: { fontSize: 8.5 },
        headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
        columnStyles: {
          0: { cellWidth: 43 },
          1: { cellWidth: 24 },
          2: { cellWidth: 24 },
        },
      });

      accesoriosSeleccionadosY = doc.lastAutoTable
        ? doc.lastAutoTable.finalY
        : 160;
      //PLAN SELECCIONADO
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.setFillColor(255, 0, 0);
      doc.rect(10, yPlan, 91, 7, "F"); // Rectángulo para encabezado del plan seleccionado
      doc.text("Plan Seleccionado", 58, yPlan + 5, { align: "center" });

      // Verifica si hay un plan seleccionado antes de generar la tabla
      if (selectedPlan) {
        const planDetails = [
          ["Tipo:", selectedPlan.title],
          [
            "Precio Total:",
            `$${selectedPlan.totalPrice.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
          ],
          [
            "Accesorios incluidos:",
            selectedPlan?.accessories?.length
              ? selectedPlan.accessories.map((acc) => acc.title).join(", ")
              : "Ninguno",
          ],
        ];

        autoTable(doc, {
          startY: yPlan + 7,
          margin: { left: 10 },
          head: [["Dato", "Descripción"]],
          body: planDetails,
          theme: "plain",
          styles: { fontSize: 8.5 },
          headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 51 },
          },
        });
      } else {
        // Mensaje en caso de que no haya un plan seleccionado
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(
          "No se ha seleccionado ningún plan.",
          14,
          doc.lastAutoTable.finalY + 25
        );
      }
    }

    // Calculamos la posición
    let posicionDatosSeguro = accesoriosSeleccionadosY + 10; // Si hay muchos accesorios
    let posicionPago = posicionDatosSeguro;

    if (posicionDatosSeguro > 250) {
      //para evitar que exceda el espacio de la página
      doc.addPage();
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(`${folioVenta}3`, 10, 10);
      doc.text(`${fecha_creacion}3`, 35, 10);
      band = 1;
      posicionDatosSeguro = 20; // Reiniciamos la posición al inicio de la nueva página
      posicionPago = posicionDatosSeguro + 60;
    } else {
      if (numAccesorios > 25 || (numAccesorios >= 17 && numAccesorios <= 24)) {
        band = 1;
        posicionDatosSeguro = 14; // Reiniciamos la posición al inicio de la nueva página
        const yFinal = doc.lastAutoTable.finalY + 10;

        // Encabezado del plan
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.setFillColor(255, 0, 0);
        doc.rect(10, yFinal, 91, 7, "F"); // Rectángulo para encabezado del plan seleccionado
        doc.text("Plan Seleccionado", 58, yFinal + 5, { align: "center" });

        // Verifica si hay un plan seleccionado antes de generar la tabla
        if (selectedPlan) {
          const planDetails = [
            ["Tipo:", selectedPlan.title],
            [
              "Precio Total:",
              `$${selectedPlan.totalPrice.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
            ],
            [
              "Accesorios incluidos:",
              selectedPlan.accessories.join(", ") || "Ninguno",
            ],
          ];

          autoTable(doc, {
            startY: yFinal + 7,
            margin: { left: 10 },
            head: [["Dato2", "Descripción"]],
            body: planDetails,
            theme: "plain",
            styles: { fontSize: 8.5 },
            headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
            columnStyles: {
              0: { cellWidth: 40 },
              1: { cellWidth: 51 },
            },
          });
          posicionPago = doc.lastAutoTable.finalY + 55;
        } else {
          // Mensaje en caso de que no haya un plan seleccionado
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.text("No se ha seleccionado ningún plan.", 14, yFinal + 20);
        }
      }
    }

    // DATOS DEL SEGURO (Posición ajustada)
    /*doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(255, 0, 0); // Fondo rojo para encabezado de seguro
    doc.rect(110, posicionDatosSeguro, 91, 7, "F"); // Encabezado de seguro
    doc.text("Datos Seguro", 155, posicionDatosSeguro + 5, { align: "center" });

    autoTable(doc, {
      startY: posicionDatosSeguro + 7, // Ajustamos justo debajo del encabezado
      margin: { left: 110 },
      head: [["Dato", "Descripción"]],
      body: [
        ["Seguro Gratis:", "PENDIENTE"],
        ["Tipo de paquete:", "PENDIENTE"],
        ["Estado:", "PENDIENTE"],
        ["Plazo:", "PENDIENTE"],
        ["Aseguradora:", "PENDIENTE"],
        ["Monto:", "PENDIENTE"],
      ],
      theme: "plain",
      styles: { fontSize: 8.5 },
      headStyles: { fillColor: [255, 0, 0], textColor: [255, 255, 255] },
      columnStyles: {
        0: { cellWidth: 42 }, // Columna "Campo"
        1: { cellWidth: 49 }, // Columna "Valor"
      },
    });*/

    // si no se ha agregado una pagina, se agrega una nueva
    if (band == 0) {
      doc.addPage();
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(`${folioVenta}`, 10, 10);
      doc.text(`${fecha_creacion}`, 35, 10);
      posicionPago = 20;
    }

    //DESGLOSE DE PAGO
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(255, 0, 0);
    doc.rect(10, posicionPago + 2, 191, 10, "F");
    doc.text("Desglose de Pagos Mensuales", 105, posicionPago + 8, {
      align: "center",
    });

    // Calcular tabla de pagos mes a mes
    let saldo = dataToSend.precio_total - dataToSend.enganche;
    const mensualidad = dataToSend.mensualidad_estimacion;
    const meses = dataToSend.meses_financiamiento;
    const fechaBase = new Date(); // Fecha actual

    // Calcular la mensualidad del vehículo y de los accesorios
    const mensualidadAccesorios = dataToSend.total_accesorios / meses;
    const mensualidadVehiculo = mensualidad - mensualidadAccesorios;

    const tablaPagos = [];
    for (let i = 1; i <= meses; i++) {
      saldo -= mensualidad;
      const fechaPago = new Date(fechaBase); // Crear una copia
      fechaPago.setMonth(fechaPago.getMonth() + i - 1);
      const fechaFormateada = `${fechaPago
        .getDate()
        .toString()
        .padStart(2, "0")}/${(fechaPago.getMonth() + 1)
          .toString()
          .padStart(2, "0")}/${fechaPago.getFullYear()}`;

      tablaPagos.push([
        `${i}`, // Periodo
        fechaFormateada, // Fecha de Pago
        `$${mensualidadVehiculo.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`, // Renta del Auto
        `$${(mensualidadVehiculo * 0.1).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`, // Seguro de Auto (Ejemplo: 10% de la renta)
        `$${mensualidadAccesorios.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`, // Accesorios (Ejemplo: 5% de la renta)
        `$${(mensualidadVehiculo * 0.02).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`, // Garantía Extendida (Ejemplo: 2% de la renta)
        `$${(mensualidadVehiculo * 0.03).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`, // Producto Adicional (Ejemplo: 3% de la renta)
        `${Number(mensualidad).toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        })}`, // Renta Mensual
        //`$${saldo > 0 ? saldo.toFixed(2) : "0.00"}`,
      ]);
    }

    autoTable(doc, {
      startY: posicionPago + 10,
      margin: { left: 10 },
      head: [
        [
          "Periodo",
          "Fecha de Pago",
          "Renta del Auto",
          "Seguro de Auto",
          "Accesorios",
          "Garantía Extendida",
          "Producto Adicional",
          "Renta Mensual",
        ],
      ],
      body: tablaPagos,
      theme: "plain",
      styles: { fontSize: 8.5 },
      headStyles: {
        fillColor: [255, 0, 0],
        halign: "center",
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 23, halign: "center" }, // Periodo
        1: { cellWidth: 27, halign: "center" }, // Fecha de Pago
        2: { cellWidth: 23, halign: "center" }, // Renta del Auto
        3: { cellWidth: 24, halign: "center" }, // Seguro de Auto
        4: { cellWidth: 23, halign: "center" }, // Accesorios
        5: { cellWidth: 23, halign: "center" }, // Garantía Extendida
        6: { cellWidth: 24, halign: "center" }, // Producto Adicional
        7: { cellWidth: 24, halign: "center" }, // Renta Mensual
      },
    });

    const pagoY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 160;
    let Desglose = pagoY + 10;
    if (Desglose > 250) {
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(`${folioVenta}`, 10, 10);
      doc.text(`${fecha_creacion}`, 35, 10);
    }

    // COMPARACION DE MENSUALIDAADES
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(255, 0, 0);
    doc.rect(10, doc.lastAutoTable.finalY + 10, 190, 10, "F");
    doc.text(
      "Comparación de Mensualidades",
      105,
      doc.lastAutoTable.finalY + 15,
      { align: "center" }
    );

    const plazos = [12, 24, 36, 48, 60];
    const saldoFinanciar = dataToSend.precio_total - dataToSend.enganche;

    const tablaComparacion = plazos.map((meses) => {
      const mensualidad = saldoFinanciar / meses;
      return [
        `${meses} meses`,
        `$${mensualidad.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      ];
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 20,
      margin: { left: 10 },
      head: [["Plazo", "Pago Mensual"]],
      body: tablaComparacion,
      theme: "plain",
      styles: { fontSize: 8.5 },
      headStyles: {
        fillColor: [255, 0, 0],
        halign: "center",
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 95, halign: "center" },
        1: { cellWidth: 95, halign: "center" },
      },
    });

    // Fecha de emisión
    const fecha = new Date().toLocaleDateString();
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Fecha de emisión: ${fecha}`, 14, 285);
    doc.text("Gracias por su preferencia.", 105, 290, { align: "center" });

    //doc.save(`Cotizacion_${dataToSend.nombre || "Cliente"}.pdf`);
    const blob = doc.output('blob');
    const fileName = `Cotizacion_Financiamiento_${dataToSend.nombre || 'Cliente'}.pdf`;
    const pdfFile = new File([blob], fileName, {
      type: 'application/pdf'
    });

    //Agrega al arreglo
    agregarArchivo(pdfFile);

    //si no se rechaza la garantia, está listo para enviar
    if (garantia_extendida === "No") {
      setReadyToSend(true);
    }

  };

  //******************************** GENERA PDF COTIZACIÓN CONTADO PENDIENTE AUN ***********************************************************/
  const generarPDFcontado = () => {
    setTipoEnvio("contado");
    const dataToSend = {
      ...formData,
      nombreCliente: selectedMailData?.nombre || "",
      marca_vehiculo: selectedMailData?.marca_vehiculo || "",
      modelo_vehiculo: selectedMailData?.modelo_vehiculo || "",
      meses_financiamiento: selectedMailData?.meses_financiamiento || "",
      porcentaje_enganche: selectedMailData?.porcentaje_enganche || "",
      mensualidad_estimacion: selectedMailData?.mensualidad_estimacion || "",
      agencia: selectedMailData?.agencia || "",
      ag_id: selectedMailData?.ag_id || "",
      precio_total: selectedMailData?.precio_vehiculo || "",
      enganche: selectedMailData?.enganche || "",
      auto: selectedMailData?.url_img || "",
      version: selectedMailData?.version_vehiculo || "",
    };

    const doc = new jsPDF();

    // Logo para encabezado y pie
    const logoURL = "/assets/img/product/Imagen1.png";
    const logoGocar = "/assets/img/product/gocar_negro.png";

    // Encabezado de la primera página
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("CARTA COTIZACION VIRTUS 2024", 105, 20, { align: "center" });
    doc.addImage(logoURL, "PNG", 180, 7, 15, 15);

    // Primer texto
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const texto = `AT'N: ${dataToSend.nombreCliente} \n\nA quien corresponda:\n\nReciba un cordial saludo, así como adjunto a la presente carta cotización solicitada:`;
    const splitTexto = doc.splitTextToSize(texto, 180);
    doc.text(splitTexto, 14, 33);

    // Imagen del auto
    //doc.addImage(selectedMailData.url_img, "PNG", 17, 65, 75, 50);

    // Tabla de info vehículo
    const infoVehiculo = [
      ["Unidad:", dataToSend.modelo_vehiculo],
      ["Marca:", dataToSend.marca_vehiculo],
      ["Modelo:", selectedMailData.ano_vehiculo],
      ["Versión:", dataToSend.version],
    ];
    doc.autoTable({
      startY: 68,
      margin: { left: 109 },
      body: infoVehiculo,
      theme: "plain",
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 30, fontStyle: "bold" },
        1: { cellWidth: 60 },
      },
    });
    const finalY = doc.lastAutoTable.finalY;
    const DescripcionY = finalY + 5;
    doc.setFont("helvetica", "bold");
    doc.text(`Descripción del vehículo:`, 111, DescripcionY);
    doc.setFont("helvetica", "normal");

    const descripcion = selectedCar.modelo ?? "";
    const lineas = doc.splitTextToSize(descripcion, 75);
    doc.text(lineas, 111, DescripcionY + 5);

    let yLista = DescripcionY + 55;

    // Lista de características
    /*const lista = [
      "Pantalla touch de 7 pulgadas y 4 bocinas.",
      "Cambio manual o automático",
      "Testigo de pérdida de presión de neumáticos",
      "Faros, luces traseras, luces de día LED.",
      "Seis bolsas de aire.",
    ];
    
    lista.forEach((item) => {
      doc.text(`•  ${item}`, 111, yLista);
      yLista += 5;
    });*/

    // Precios
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    const precio = Number(dataToSend.precio_total);
    const precioFormateado = precio.toLocaleString("es-MX", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    doc.text(`Precio Contado: $${precioFormateado}`, 14, yLista + 5);

    /*doc.text(
      "Precio con acondicionamiento de Protección total: PENDIENTE",
      14,
      yLista + 12
    );
    doc.setFont("helvetica", "normal");*/

    let accesoriosY = yLista + 18;
    let contenidoFinalY = accesoriosY; // Para saber dónde termina el contenido

    // Lista de accesorios
    if (selectedPlan && plans?.length > 0) {
      const accesoriosNombres = (selectedPlan.accessories ?? [])
        .map(acc => acc.title ?? "")
        .filter(Boolean)
        .join(", ") || "Ninguno";

      const texto2 = `Protección ${selectedPlan.title}: ${accesoriosNombres}. Al precio de: $${selectedPlan.totalPrice.toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
      const splitTexto2 = doc.splitTextToSize(texto2, 180);
      doc.text(splitTexto2, 14, accesoriosY + 2);
      accesoriosY += splitTexto2.length * 5 + 5;

      let accesorioY = accesoriosY;

      plans.forEach((acc, index) => {
        if (accesorioY > 250) {
          doc.addPage();
          accesorioY = 20;
          doc.addImage(logoURL, "PNG", 180, 7, 15, 15);
        }

        doc.setFont("helvetica", "bold");
        doc.text(acc.title, 14, accesorioY);
        accesorioY += 6;

        doc.setFont("helvetica", "normal");
        const descripcion = acc.description ?? "";;
        const splitTexto3 = doc.splitTextToSize(descripcion, 180);
        doc.text(splitTexto3, 14, accesorioY);
        accesorioY += splitTexto3.length * 5;

        doc.setFont("helvetica", "bold");
        const precioAcc = Number(acc.price);
        const precioFormateado = precioAcc.toLocaleString("es-MX", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        doc.text(`Precio: $${precioFormateado}`, 14, accesorioY);

        accesorioY += 12;
      });

      contenidoFinalY = accesorioY; // actualizamos posición final del contenido
    } else {
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text("No se ha seleccionado ningún plan.", 14, accesoriosY);
      contenidoFinalY = accesoriosY + 20;
    }

    // Si el contenido se pasa del límite, agregamos nueva página
    if (contenidoFinalY > 230) {
      doc.addPage();
      contenidoFinalY = 20; // reiniciamos Y para nueva página
      doc.addImage(logoURL, "PNG", 180, 7, 15, 15);
    }

    // === Leyenda y firma final ===
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.text(
      "Esta cotización tiene una nueva vigencia hasta (Fecha), cambios y variaciones de precios sin previo aviso",
      105,
      contenidoFinalY + 65,
      { align: "center" }
    );

    doc.setFontSize(10);
    doc.text("Atentamente:", 105, contenidoFinalY + 25, { align: "center" });
    doc.text("Elyan Yuridia Yañez Aragón.", 105, contenidoFinalY + 30, {
      align: "center",
    });
    doc.text("Móvil: 2228299725", 105, contenidoFinalY + 35, {
      align: "center",
    });
    doc.text("Mail: elyan.yanez@leonmotors.com.mx", 105, contenidoFinalY + 40, {
      align: "center",
    });

    // === Footer fijo en cada página ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.addImage(logoGocar, "PNG", 4, 288, 23, 7.5);
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.text(
        "RFC: LMO860212R76   Leon Motors S.A. de C.V. RFC: LMO860212R76. Blvd Adolfo Lopez Mateos 2004 Pte, colonia Peñitas, C.P. 37160 Leon Gto. (477) 2146100",
        105,
        290,
        { align: "center" }
      );
    }

    //doc.save(`Cotizacion_Contado_${dataToSend.nombreCliente || "Cliente"}.pdf`);
    const blob = doc.output('blob');
    const fileName = `Cotizacion_Contado_${dataToSend.nombre || 'Cliente'}.pdf`;
    const pdfFile = new File([blob], fileName, {
      type: 'application/pdf'
    });

    //Agrega al arreglo
    agregarArchivo(pdfFile);

    //si no se rechaza la garantia, está listo para enviar
    if (garantia_extendida === "No") {
      setReadyToSend(true);
    }
  };

  //******************************** GENERA PDF RENUNCIA ***********************************************************/
  const generarPDFrenuncia = async () => {
    if (selectedMailData.depende_auto === "Si") {
      setTipoEnvio("rechazoContado");
    }

    setTipoEnvio("rechazoCotizacion");

    function abrirVentanaParaFirmar(pdfDoc, nombreArchivo, nombreCliente, agregarArchivo) {
      return new Promise((resolve) => {

        const nuevaVentana = window.open("", "_blank");

        if (!nuevaVentana) {
          alert(
            "El navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para continuar."
          );
          console.error("Bloqueo de ventana detectado.");
          return;
        }

        const checkIfDocumentReady = setInterval(async () => {
          if (nuevaVentana.document) {
            clearInterval(checkIfDocumentReady);

            const pdfEditableBytes = await pdfDoc.save();
            const pdfEditableBlob = new Blob([pdfEditableBytes], {
              type: "application/pdf",
            });
            const pdfEditableUrl = URL.createObjectURL(pdfEditableBlob);
            nuevaVentana.document.body.innerHTML = `<embed src="${pdfEditableUrl}" width="100%" height="100%">`;

            setTimeout(() => {
              function crearCanvasFirma(topPos, textoGuia) {
                const canvas = nuevaVentana.document.createElement("canvas");
                canvas.width = 300;
                canvas.height = 130;
                canvas.style.position = "absolute";
                canvas.style.top = topPos + "px";
                canvas.style.left = "100px";
                canvas.style.backgroundColor = "#EDF0F5";
                canvas.style.border = "1px solid #A3ADC2";
                canvas.style.borderRadius = "8px";
                nuevaVentana.document.body.appendChild(canvas);
                setTimeout(() => {
                  const ctx = canvas.getContext("2d");
                  ctx.font = "16px Arial";
                  ctx.fillStyle = "#4A4A4A";
                  ctx.fillText(textoGuia, 20, 30);
                }, 10);
                return canvas;
              }

              function restaurarGuia(signaturePad, canvas, texto) {
                if (signaturePad.isEmpty()) {
                  const ctx = canvas.getContext("2d");
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  ctx.font = "16px Arial";
                  ctx.fillStyle = "#4A4A4A";
                  ctx.fillText(texto, 20, 30);
                }
              }

              function limpiarGuiaSinBorrarFirma(canvas, signaturePad) {
                const data = signaturePad.toData();
                signaturePad.clear();
                signaturePad.fromData(data);
              }

              function verificarTodoCompleto() {
                const firmasCompletas =
                  !signaturePadCliente.isEmpty() && !signaturePadAsesor.isEmpty();
                if (firmasCompletas) {
                  btnGuardarFirma.disabled = false;
                  btnGuardarFirma.style.opacity = "1";
                  btnGuardarFirma.style.cursor = "pointer";
                } else {
                  btnGuardarFirma.disabled = true;
                  btnGuardarFirma.style.opacity = "0.6";
                  btnGuardarFirma.style.cursor = "not-allowed";
                }
              }

              const canvasCliente = crearCanvasFirma(210, "Firma del Cliente");
              const canvasAsesor = crearCanvasFirma(440, "Firma del Asesor");

              const signaturePadCliente = new SignaturePad(canvasCliente, {
                minWidth: 0.5,
                maxWidth: 1.3,
              });
              const signaturePadAsesor = new SignaturePad(canvasAsesor, {
                minWidth: 0.5,
                maxWidth: 1.3,
              });

              const estiloBoton = `
                background-color: #B5BDD0;
                color: 2A2A2A;
                border: none;
                border-radius: 8px;
                padding: 10px 16px;
                font-size: 14px;
                cursor: pointer;
                transition: background-color 0.3s ease;
              `;

              const aplicarEstiloBoton = (boton, top, left) => {
                boton.style.cssText = estiloBoton + `position: absolute; top: ${top}; left: ${left};`;
                boton.addEventListener("mouseenter", () => {
                  boton.style.backgroundColor = "#A0A9BF";
                });
                boton.addEventListener("mouseleave", () => {
                  boton.style.backgroundColor = "#B5BDD0";
                });
              };

              const btnLimpiarCliente = nuevaVentana.document.createElement("button");
              btnLimpiarCliente.textContent = "Limpiar Cliente";
              aplicarEstiloBoton(btnLimpiarCliente, "370px", "190px");
              nuevaVentana.document.body.appendChild(btnLimpiarCliente);

              const btnLimpiarAsesor = nuevaVentana.document.createElement("button");
              btnLimpiarAsesor.textContent = "Limpiar Asesor";
              aplicarEstiloBoton(btnLimpiarAsesor, "600px", "190px");
              nuevaVentana.document.body.appendChild(btnLimpiarAsesor);

              const btnGuardarFirma = nuevaVentana.document.createElement("button");
              btnGuardarFirma.textContent = "Guardar";
              aplicarEstiloBoton(btnGuardarFirma, "680px", "211px");
              nuevaVentana.document.body.appendChild(btnGuardarFirma);

              btnGuardarFirma.disabled = true;
              btnGuardarFirma.style.opacity = "0.6";
              btnGuardarFirma.style.cursor = "not-allowed";

              signaturePadCliente.addEventListener("endStroke", verificarTodoCompleto);
              signaturePadAsesor.addEventListener("endStroke", verificarTodoCompleto);

              btnLimpiarCliente.onclick = () => {
                signaturePadCliente.clear();
                restaurarGuia(signaturePadCliente, canvasCliente, "Firma del Cliente");
                verificarTodoCompleto();
              };

              btnLimpiarAsesor.onclick = () => {
                signaturePadAsesor.clear();
                restaurarGuia(signaturePadAsesor, canvasAsesor, "Firma del Asesor");
                verificarTodoCompleto();
              };

              btnGuardarFirma.onclick = async function () {
                limpiarGuiaSinBorrarFirma(canvasCliente, signaturePadCliente);
                limpiarGuiaSinBorrarFirma(canvasAsesor, signaturePadAsesor);

                const firmaClienteBytes = await fetch(signaturePadCliente.toDataURL()).then((res) => res.arrayBuffer());
                const firmaAsesorBytes = await fetch(signaturePadAsesor.toDataURL()).then((res) => res.arrayBuffer());

                const firmaClienteImg = await pdfDoc.embedPng(firmaClienteBytes);
                const firmaAsesorImg = await pdfDoc.embedPng(firmaAsesorBytes);

                const paginas = pdfDoc.getPages();
                paginas.slice(0, 4).forEach((page) => {
                  page.drawImage(firmaAsesorImg, {
                    x: 90,
                    y: 310,
                    width: 135,
                    height: 65,
                  });
                  page.drawImage(firmaClienteImg, {
                    x: 350,
                    y: 310,
                    width: 135,
                    height: 65,
                  });
                });

                const pdfFirmadoBytes = await pdfDoc.save();
                const pdfFirmadoBlob = new Blob([pdfFirmadoBytes], {
                  type: "application/pdf",
                });

                // Crear File y agregarlo al estado
                const nombreCompleto =
                  `${nombreArchivo}_${nombreCliente || 'Cliente'}.pdf`;
                const pdfFile = new File([pdfFirmadoBlob], nombreCompleto, {
                  type: 'application/pdf',
                });
                agregarArchivo(pdfFile);

                const pdfFirmadoUrl = URL.createObjectURL(pdfFirmadoBlob);
                nuevaVentana.document.body.innerHTML =
                  `<embed src="${pdfFirmadoUrl}" width="100%" height="100%">`;

                setTimeout(() => {
                  nuevaVentana.close();
                  resolve();
                }, 500);
              };
            }, 500);
          }
        }, 100);
      });
    }

    const dataToSend = {
      ...formData,
      mensualidad_estimacion: selectedMailData?.mensualidad_estimacion || "",
      nombreCliente: selectedMailData?.nombre || "",
      nombreAsesor: selectedMailData?.asesor || "",
      agencyName: selectedMailData?.agencia || "",
    };

    const logoURL = "/assets/img/product/gocar_negro.png";
    const encabezado1 = "/assets/img/product/encabezado1.png";
    const encabezado2 = "/assets/img/product/encabezado2.png";

    const fechaActual = new Date().toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    function agregarEncabezado(doc) {
      doc.addImage(encabezado2, "PNG", 108, 0, 108, 20);
      doc.addImage(encabezado1, "PNG", 0, 0, 125, 20);
      doc.addImage(logoURL, "PNG", 108, 7, 55, 16);
      doc.setFontSize(13);
      doc.text("CARTA RENUNCIA", 105, 29, { align: "center" });
    }

    function agregarLugarYFecha(doc, agencyName, fechaActual) {
      doc.setFontSize(10);
      doc.text(`Lugar y Fecha: ${agencyName}, ${fechaActual}`, 98, 37);
      doc.line(122, 38, 195, 38);
    }

    function agregarPiePagina(doc) {
      doc.setFillColor(231, 230, 230);
      doc.rect(0, 280, 210, 20, "F");
      doc.setFontSize(10);
      doc.text(
        "C. Gabriel Mancera 1629, Col del Valle Sur, Benito Juárez, 03104 Ciudad de México, CDMX. Tel. 55 5681 8736",
        105,
        290,
        { align: "center" }
      );
    }

    function agregarFirmas(doc, nombreAsesor, nombreCliente, yBase) {
      // Firma asesor
      doc.text(nombreAsesor, 30, yBase + 49);
      doc.text("Nombre y firma del asesor y/o F&I.", 25, yBase + 56);
      doc.line(20, yBase + 50, 85, yBase + 50);

      // Firma cliente
      doc.text(nombreCliente, 120, yBase + 49);
      doc.text("Nombre y firma del cliente.", 120, yBase + 56);
      doc.line(110, yBase + 50, 175, yBase + 50);
    }


    //***************************** PDF COBERTURA AMPLIA *************************/
    const generarPDFcoberturaAmplia = async () => {
      const doc = new jsPDF();

      agregarEncabezado(doc);

      //lugar y fecha
      agregarLugarYFecha(doc, dataToSend.agencyName, fechaActual);

      //Agregar primer texto
      doc.setFontSize(10);
      const texto = `Por medio del presente, yo: ${dataToSend.nombreCliente} hago constar que fue ofrecida una Cobertura Mecánica con el vehículo que estoy adquiriendo y que me fueron explicados los siguientes beneficios:`;
      const splitTexto = doc.splitTextToSize(texto, 180);
      doc.text(splitTexto, 14, 46);

      // Primer recuadro
      doc.setFillColor(214, 220, 229); // Color gris claro
      doc.roundedRect(14, 55, 180, 15, 5, 5, "F"); // (x, y, width, height, radioX, radioY)

      const texto10 = `Cobertura Amplia (para vehículos menores a 4 años de antigüedad o con hasta 80,00kms.)\nCubre costos de materiales y mano de obra necesarios para reparar o remplazar las partes o componentes mencionados a continuación.`;
      const splitTexto10 = doc.splitTextToSize(texto10, 165);
      doc.text(splitTexto10, 20, 60);

      doc.setFillColor(214, 220, 229); // Color gris claro
      doc.roundedRect(14, 75, 180, 50, 5, 5, "F");

      //Lista de beneficios en recuadro 2
      const lista = [
        "Motor",
        "Cambio manual o automático",
        "Sistema eléctrico",
        "Inyección y alimentación",
        "Dirección",
        "Suspensión",
        "Sistema de enfriamiento",
        "Sistema de frenos",
        "Cobertura de hasta $90,000.00 M.N. en repuestos y reparaciones.",
      ];

      let yLista5 = 80;
      lista.forEach((item) => {
        doc.text(`• ${item}`, 20, yLista5);
        yLista5 += 5;
      });

      //Segundo recuadro
      doc.setFillColor(214, 220, 229); // Color gris claro
      doc.roundedRect(14, yLista5 + 5, 180, 35, 5, 5, "F");
      const texto7 = `Cobertura de Tren Motriz (para vehículos menores a 10 años de antigüedad o con hasta 150,000 kms.)\nCubre costos de materiales y mano de obra necesarios para reparar o remplazar las partes o componentes mencionados a continuación.`;
      const splitTexto7 = doc.splitTextToSize(texto7, 165);
      doc.text(splitTexto7, 20, yLista5 + 10);

      // Lista de beneficios en recuadro 2
      const lista8 = [
        "Motor",
        "Cambio manual o automático",
        "Cobertura de hasta $90,000.00 en repuestos y reparaciones.",
      ];

      yLista5 += 26;
      lista8.forEach((item) => {
        doc.text(`• ${item}`, 20, yLista5);
        yLista5 += 5;
      });

      //Agregar primer texto
      const texto9 = `Dicha cobertura no tiene pago de deducible ni trámites engorrosos, es transferible si decido vender mi vehículo, tiene cobertura en toda la República Mexicana, es aplicable desde el primer día y está incluida en mi financiamiento con un costo de ${Number(
        dataToSend.mensualidad_estimacion
      ).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
      })} al mes.\nConociendo los beneficios anteriores, RENUNCIO a la Cobertura Mecánica ofrecida y confirmo que no tengo interés en que sea agregarla a mi financiamiento.`;
      const splitTexto9 = doc.splitTextToSize(texto9, 180);
      doc.text(splitTexto9, 14, yLista5 + 10);

      // Espacio para firma del asesor
      agregarFirmas(doc, dataToSend.nombreAsesor, dataToSend.nombreCliente, 200);

      agregarPiePagina(doc);

      //doc.save(`Renuncia_Cobertura_Amplia_${dataToSend.nombreCliente}.pdf`);

      const pdfBytes = doc.output("arraybuffer");
      const pdfDoc = await PDFDocument.load(pdfBytes);
      await abrirVentanaParaFirmar(pdfDoc, `Renuncia_Firmada_C.Amplia_${dataToSend.nombreCliente}`, dataToSend.nombreCliente, agregarArchivo);
    }

    //***************************** PDF COBERTURA MOVILIDAD *************************/
    const generarPDFcoberturaMovilidad = async () => {
      const doc = new jsPDF();

      agregarEncabezado(doc);

      //lugar y fecha
      agregarLugarYFecha(doc, dataToSend.agencyName, fechaActual);

      //Agregar primer texto
      doc.setFontSize(10);
      const texto = `Por medio del presente, yo: ${dataToSend.nombreCliente} hago constar que fue ofrecida una Cobertura Mecánica con el vehículo que estoy adquiriendo y que me fueron explicados los siguientes beneficios:`;
      const splitTexto = doc.splitTextToSize(texto, 180);
      doc.text(splitTexto, 14, 46);

      let yLista10 = 60;

      //Segundo recuadro
      doc.setFillColor(214, 220, 229); // Color gris claro
      doc.roundedRect(14, yLista10, 180, 50, 5, 5, "F");
      const texto18 = `o   Garantía de Movilidad (para vehículos nuevos y seminuevos.)`;
      const splitTexto18 = doc.splitTextToSize(texto18, 165);
      doc.text(splitTexto18, 27, 70);

      const texto19 = `Garantizamos tu movilidad vehicular en caso de accidente o pérdida total por daños o robo.\nDos opciones de movilidad: Vehículos de transporte con conductor (UBER) con un monto máximo de $300.00 M.N. por día o Auto Sustituto.`;
      const splitTexto19 = doc.splitTextToSize(texto19, 165);
      doc.text(splitTexto19, 20, 75);

      // Lista de beneficios en recuadro 2
      const lista12 = ["Accidente Vehicular", "Movilidad hasta por 10 días", "Pérdida Total", "Movilidad hasta por 30 días",];

      yLista10 += 30;
      const yInicial = yLista10;
      lista12.forEach((item) => {
        doc.text(`• ${item}`, 27, yLista10);
        yLista10 += 5;
      });

      const texto22 = `Dichas coberturas no tiene pago de deducible ni trámites engorrosos, es transferible si decido vender mi vehículo, tiene cobertura en toda la República Mexicana, es aplicable desde el primer día y está incluida en mi financiamiento con un costo de ${Number(
        dataToSend.mensualidad_estimacion
      ).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
      })} al mes.\nConociendo los beneficios anteriores, RENUNCIO a la Cobertura Mecánica ofrecida y confirmo que no tengo interés en que sea agregarla a mi financiamiento.`;
      const splitTexto22 = doc.splitTextToSize(texto22, 180);
      doc.text(splitTexto22, 14, yLista10 + 10);

      // Espacio para firma del asesor
      agregarFirmas(doc, dataToSend.nombreAsesor, dataToSend.nombreCliente, 178);

      agregarPiePagina(doc);

      //Obtener bytes del PDF y cargar con pdf-lib para edición
      const pdfBytes = doc.output("arraybuffer");
      const pdfDoc = await PDFDocument.load(pdfBytes);

      //Abrir la ventana para firma
      await abrirVentanaParaFirmar(pdfDoc, `Renuncia_Firmada_Movilidad_${dataToSend.nombreCliente}`, dataToSend.nombreCliente, agregarArchivo)
    }

    //***************************** PDF COBERTURA LLAVES *************************/
    const generarPDFcoberturaLlaves = async () => {
      const doc = new jsPDF();

      agregarEncabezado(doc);

      //lugar y fecha
      agregarLugarYFecha(doc, dataToSend.agencyName, fechaActual);

      //lugar y fecha
      doc.setFontSize(10);
      doc.text(`Lugar y Fecha: ${dataToSend.agencyName}, ${fechaActual}`, 98, 37);
      doc.line(122, 38, 195, 38);

      //Agregar primer texto
      doc.setFontSize(10);
      const texto = `Por medio del presente, yo: ${dataToSend.nombreCliente} hago constar que fue ofrecida una Cobertura Mecánica con el vehículo que estoy adquiriendo y que me fueron explicados los siguientes beneficios:`;
      const splitTexto = doc.splitTextToSize(texto, 180);
      doc.text(splitTexto, 14, 46);

      let yLista10 = 60;

      //tercer recuadro
      doc.setFillColor(214, 220, 229); // Color gris claro
      doc.roundedRect(14, yLista10, 180, 35, 5, 5, "F");

      const texto20 = `o   Cobertura de Llaves (para vehículos nuevos.)`;
      const splitTexto20 = doc.splitTextToSize(texto20, 180);
      doc.text(splitTexto20, 27, 70);

      const texto21 = `Cubre el reemplazo de tu llave o sensor en caso de pérdida o daño irreparable.\nCobertura de hasta $6,000.00 M.N.\nAtención y reemplazo original de agencia.`;
      const splitTexto21 = doc.splitTextToSize(texto21, 180);
      doc.text(splitTexto21, 20, yLista10 + 19);

      const texto22 = `Dichas coberturas no tienen pago de deducible ni trámites engorrosos, son transferibles si decido vender mi vehículo, tiene cobertura en toda la República Mexicana, es aplicable desde el primer día y está incluida en mi financiamiento con un costo de ${Number(
        dataToSend.mensualidad_estimacion
      ).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
      })} al mes.\nConociendo los beneficios anteriores, RENUNCIO a la Cobertura Mecánica ofrecida y confirmo que no tengo interés en que sea agregarla a mi financiamiento.`;
      const splitTexto22 = doc.splitTextToSize(texto22, 180);
      doc.text(splitTexto22, 14, yLista10 + 48);

      // Espacio para firma del asesor
      agregarFirmas(doc, dataToSend.nombreAsesor, dataToSend.nombreCliente, 170);
      agregarPiePagina(doc);

      //doc.save(`Renuncia_Cobertura_Amplia_${dataToSend.nombreCliente}.pdf`);

      //Obtener bytes del PDF y cargar con pdf-lib para edición
      const pdfBytes = doc.output("arraybuffer");
      const pdfDoc = await PDFDocument.load(pdfBytes);
      await abrirVentanaParaFirmar(pdfDoc, `Renuncia_Firmada_C.Llaves_${dataToSend.nombreCliente}`, dataToSend.nombreCliente, agregarArchivo)
    }

    //***************************** PDF COBERTURA LLANTAS *************************/
    const generarPDFcoberturaLlantas = async () => {
      const doc = new jsPDF();

      agregarEncabezado(doc);

      //lugar y fecha
      agregarLugarYFecha(doc, dataToSend.agencyName, fechaActual);;

      //lugar y fecha
      doc.setFontSize(10);
      doc.text(`Lugar y Fecha: ${dataToSend.agencyName}, ${fechaActual}`, 98, 37);
      doc.line(122, 38, 195, 38);

      //Agregar primer texto
      doc.setFontSize(10);
      const texto = `Por medio del presente, yo: ${dataToSend.nombreCliente} hago constar que fue ofrecida una Cobertura Mecánica con el vehículo que estoy adquiriendo y que me fueron explicados los siguientes beneficios:`;
      const splitTexto = doc.splitTextToSize(texto, 180);
      doc.text(splitTexto, 14, 46);

      // Primer recuadro
      doc.setFillColor(214, 220, 229); // Color gris claro
      doc.roundedRect(14, 64, 180, 65, 5, 5, "F");
      const texto25 = `o   Cobertura de Llantas (para vehículos nuevos.)`;
      const splitTexto25 = doc.splitTextToSize(texto25, 165);
      doc.text(splitTexto25, 27, 72);
      const texto17 = `Cubre el reemplazo de neumáticos en caso de daño accidental en el camino por bache, banquetazo o corte.\nHasta 2 eventos por año.\nInstalación del neumático sin costo y en menos de 48hrs.\nReposición del neumático de las mismas características.\nCobertura transferible si el vehículo cambia de propietario.`;
      const splitTexto17 = doc.splitTextToSize(texto17, 165);
      doc.text(splitTexto17, 20, 78);

      //Lista de beneficios en recuadro 2
      const lista10 = [
        "Plan 4OOO: Hasta 4 neumáticos por evento y hasta $4,000.00 M.N por neumático",
        "Plan 5OOO: Hasta 2 neumáticos y/o rines por evento y hasta $5,000.00 M.N por neumático y/o rin.",
        "Plan 12OOO: Hasta 2 neumáticos y/o rines por evento y hasta $12,000.00 M.N por neumático y/o rin",
      ];
      let yLista10 = 109;
      lista10.forEach((item) => {
        doc.text(`• ${item}`, 20, yLista10);
        yLista10 += 5;
      });

      const texto22 = `Dichas coberturas no tiene pago de deducible ni trámites engorrosos, son transferibles si decido vender mi vehículo, tiene cobertura en toda la República Mexicana, es aplicable desde el primer día y está incluida en mi financiamiento con un costo de ${Number(
        dataToSend.mensualidad_estimacion
      ).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
      })} al mes.\nConociendo los beneficios anteriores, RENUNCIO a la Cobertura Mecánica ofrecida y confirmo que no tengo interés en que sea agregarla a mi financiamiento.`;
      const splitTexto22 = doc.splitTextToSize(texto22, 180);
      doc.text(splitTexto22, 14, yLista10 + 20);

      // Espacio para firma del asesor
      agregarFirmas(doc, dataToSend.nombreAsesor, dataToSend.nombreCliente, 178);
      agregarPiePagina(doc);

      //doc.save(`Renuncia_Cobertura_Amplia_${dataToSend.nombreCliente}.pdf`);
      //Obtener bytes del PDF y cargar con pdf-lib para edición
      const pdfBytes = doc.output("arraybuffer");
      const pdfDoc = await PDFDocument.load(pdfBytes);
      //Abrir la ventana para firma
      await abrirVentanaParaFirmar(pdfDoc, `Renuncia_Firmada_C.Llantas_${dataToSend.nombreCliente}`, dataToSend.nombreCliente, agregarArchivo)
    }

    const garantiasIncluidas = planSeleccionado?.accessories?.map(acc => acc.title || acc) || [];

    const garantias = [
      { nombres: ["Cobertura Amplia", "Cobertura Amplia C4 - 1 año", "Cobertura Amplia C4 - 2 años", "Cobertura Amplia C6 - 1 año", "Cobertura Amplia C6 - 2 años", "Tren motriz", "Tren Motriz - 1 año", "Tren Motriz - 2 años", "Tren Motriz - 3 años"], generar: generarPDFcoberturaAmplia },
      { nombres: ["Garantía de Movilidad", "Garantía de Movilidad - 1 año"], generar: generarPDFcoberturaMovilidad },
      { nombres: ["Cobertura de Llaves", "Cobertura de Llaves - 1 año"], generar: generarPDFcoberturaLlaves },
      { nombres: ["Cobertura de Llantas Plan", "Plan 4000 - 1 año", "Plan 5000 - 1 año", "Plan 12000 - 1 año"], generar: generarPDFcoberturaLlantas },
    ];

    //ejecuta solo las que no vienen incluidas
    const promesas = garantias
      .filter((g) => !g.nombres.some((n) => garantiasIncluidas.includes(n)))
      .map((g) => g.generar());

    //Espera a que todas terminen
    await Promise.all(promesas);
    setReadyToSend(true); //listo para enviar

  };

  //Envía todo al backend cuando actualice 
  useEffect(() => {
    if (readyToSend && archivos.length) {
      enviarArchivos();
      setReadyToSend(false); // resetea
    }
  }, [readyToSend, archivos]);

  async function enviarArchivos() {
    if (!archivos.length) {
      console.warn('No hay archivos por enviar');
      return;
    }

    const userEmail = localStorage.getItem("userEmail") ?? "USERNAME@GMAIL.COM"; // obtener correo de asesor

    //Construir FormData 
    const formData = new FormData();

    // Datos a enviar
    formData.append('access_token', accessToken);
    formData.append('cliente', selectedMailData?.nombre);
    formData.append('correoCliente', selectedMailData?.correo_electronico);
    formData.append('correoAsesor', userEmail); //cambiar asesor
    formData.append('tipo_envio', tipoEnvio);

    // Agregar todos los archivos
    archivos.forEach((file) => {
      formData.append('archivo[]', file);
    });

    console.log("Enviando datos del vehículo y archivos:", formData);

    try {
      const response = await fetch(apiPDF, {
        method: "POST",
        body: formData,
      });

      const json = await response.json();

      if (response.ok && json.statusCode === 200 && json.data?.type === 'OK') {
        //alert('¡Listo! Sus archivos se han enviado a su correo exitosamente.');
        setArchivos([]);      // limpia si todo salio bien
      } else {
        alert(`Error (${json.statusCode}): ${json.data?.description || 'Sin mensaje'}`);
      }
    } catch (err) {
      console.error(err);
      alert('Error de red o servidor');
    }
  }


  //****************************** GENERAR PDF ACEPTACIÓN *********************************************************
  const generarPDFAceptacion = () => {
    return new Promise(async (resolve, reject) => {
      const dataToSend = {
        ...formData,
        mensualidad_estimacion: selectedMailData?.mensualidad_estimacion || "",
        nombreCliente: selectedMailData?.nombre || "",
        asesor: selectedMailData?.asesor || "",
      };

      const doc = new jsPDF();

      // Agregar logo y título
      const logoURL = "/assets/img/product/gocar_negro.png";
      doc.addImage(logoURL, "PNG", 14, 10, 40, 10.6);
      doc.setFontSize(20);
      doc.text("Rechazo de Garantía Extendida", 105, 30, { align: "center" });
      doc.setLineWidth(0.5);
      doc.line(14, 35, 196, 35);
      doc.setFontSize(12);

      const textoIntro = `Por medio del presente, yo: ${dataToSend.nombreCliente} hago constar que me fue ofrecida la Póliza de seguro GOCAR para mi auto, así como los beneficios exclusivos que ofrece, entre ellos:`;
      const splitIntro = doc.splitTextToSize(textoIntro, 180);
      doc.text(splitIntro, 14, 45);

      const beneficios = [
        "Valor factura para autos nuevos.",
        "Valor convenido para Seminuevos: Valor convenio es más alto que el valor comercial, lo que hace que ante un siniestro el pago de la indemnización de mi auto se devalúe menos.",
        "Responsabilidad civil hasta por $3,500,000: Monto mínimo permitido para circular en carreteras federales.",
        "Reparación en agencia: Permite que mi vehículo no pierda su garantía con la marca.",
        "Reparación en agencia de modelos hasta 10 años atrás: Genera valor de reventa por la calidad del trabajo y evita que el auto pierda garantía.",
        "Asesoramiento en caso de siniestro.",
        "Atención en su renovación: Previene que mi patrimonio quede desprotegido.",
      ];

      let y = 45 + splitIntro.length * 5 + 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      beneficios.forEach((item) => {
        const splitItem = doc.splitTextToSize(`• ${item}`, 180);
        doc.text(splitItem, 18, y);
        y += splitItem.length * 5 + 5;
      });

      const textoRenuncia = `Dicho beneficio es válido en toda la República Mexicana, es aplicable desde el primer día y tiene un costo de ${Number(
        dataToSend.mensualidad_estimacion
      ).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
      })} al mes.\n\nConociendo los beneficios anteriores, RENUNCIO a los beneficios de la Póliza ofrecida y confirmo que no tengo interés en que sea agregarla a mi financiamiento ni compra de contado.`;
      const splitRenuncia = doc.splitTextToSize(textoRenuncia, 180);
      doc.text(splitRenuncia, 14, y + 5);

      const firmaY = 200;
      doc.text(
        `Nombre y firma del asesor y/o F&I:\n${dataToSend.asesor}`,
        14,
        firmaY
      );
      doc.line(14, firmaY + 15, 100, firmaY + 15);
      doc.text(
        `Nombre y firma del cliente:\n\n\n${dataToSend.nombreCliente}`,
        14,
        firmaY + 30
      );
      doc.line(14, firmaY + 45, 100, firmaY + 45);
      const fecha = new Date().toLocaleDateString();
      doc.setFontSize(10);
      doc.text(`Fecha: ${fecha}`, 14, firmaY + 65);

      //************** GENERA PDF PARA FIRMA  ****************/
      const pdfBytes = doc.output("arraybuffer");
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const page = pdfDoc.getPages()[0];

      const nuevaVentana = window.open("", "_blank");
      if (!nuevaVentana) {
        alert("No se pudo abrir la ventana.");
        return reject();
      }

      const pdfEditableBytes = await pdfDoc.save();
      const pdfEditableBlob = new Blob([pdfEditableBytes], {
        type: "application/pdf",
      });
      const pdfEditableUrl = URL.createObjectURL(pdfEditableBlob);
      nuevaVentana.document.body.innerHTML = `<embed src="${pdfEditableUrl}" width="100%" height="100%">`;

      setTimeout(() => {
        function crearCanvasFirma(topPos, textoGuia) {
          const canvas = nuevaVentana.document.createElement("canvas");
          canvas.width = 300;
          canvas.height = 130;
          canvas.style.position = "absolute";
          canvas.style.top = topPos + "px";
          canvas.style.left = "100px";
          canvas.style.backgroundColor = "rgba(30, 30, 30, 0.5)";
          canvas.style.border = "1px solid #e57373";
          canvas.style.borderRadius = "8px";
          nuevaVentana.document.body.appendChild(canvas);
          setTimeout(() => {
            const ctx = canvas.getContext("2d");
            ctx.font = "16px Arial";
            ctx.fillStyle = "#e57373";
            ctx.fillText(textoGuia, 20, 30);
          }, 10);
          return canvas;
        }

        function restaurarGuia(signaturePad, canvas, texto) {
          if (signaturePad.isEmpty()) {
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.font = "16px Arial";
            ctx.fillStyle = "#e57373";
            ctx.fillText(texto, 20, 30);
          }
        }

        function limpiarGuiaSinBorrarFirma(canvas, signaturePad) {
          const data = signaturePad.toData();
          signaturePad.clear();
          signaturePad.fromData(data);
        }

        function verificarTodoCompleto() {
          const firmasCompletas =
            !signaturePadCliente.isEmpty() && !signaturePadAsesor.isEmpty();
          if (firmasCompletas) {
            btnGuardarFirma.disabled = false;
            btnGuardarFirma.style.opacity = "1";
            btnGuardarFirma.style.cursor = "pointer";
          } else {
            btnGuardarFirma.disabled = true;
            btnGuardarFirma.style.opacity = "0.6";
            btnGuardarFirma.style.cursor = "not-allowed";
          }
        }

        const canvasCliente = crearCanvasFirma(210, "Firma del Cliente");
        const canvasAsesor = crearCanvasFirma(440, "Firma del Asesor");
        const signaturePadCliente = new SignaturePad(canvasCliente, {
          minWidth: 0.5,
          maxWidth: 1.3,
        });
        const signaturePadAsesor = new SignaturePad(canvasAsesor, {
          minWidth: 0.5,
          maxWidth: 1.3,
        });

        const estiloBoton = `
            background-color: #ef5350;
            color: white;
            border: none;
            border-radius: 8px;
            padding: 10px 16px;
            font-size: 14px;
            cursor: pointer;
            transition: background-color 0.3s ease;
          `;

        const aplicarEstiloBoton = (boton, top, left) => {
          boton.style.cssText =
            estiloBoton + `position: absolute; top: ${top}; left: ${left};`;
          boton.addEventListener("mouseenter", () => {
            boton.style.backgroundColor = "#c62828";
          });
          boton.addEventListener("mouseleave", () => {
            boton.style.backgroundColor = "#ef5350";
          });
        };

        const btnLimpiarCliente = nuevaVentana.document.createElement("button");
        btnLimpiarCliente.textContent = "Limpiar Cliente";
        aplicarEstiloBoton(btnLimpiarCliente, "370px", "190px");
        nuevaVentana.document.body.appendChild(btnLimpiarCliente);

        const btnLimpiarAsesor = nuevaVentana.document.createElement("button");
        btnLimpiarAsesor.textContent = "Limpiar Asesor";
        aplicarEstiloBoton(btnLimpiarAsesor, "600px", "190px");
        nuevaVentana.document.body.appendChild(btnLimpiarAsesor);

        const btnGuardarFirma = nuevaVentana.document.createElement("button");
        btnGuardarFirma.textContent = "Guardar";
        aplicarEstiloBoton(btnGuardarFirma, "680px", "211px");
        nuevaVentana.document.body.appendChild(btnGuardarFirma);

        btnGuardarFirma.disabled = true;
        btnGuardarFirma.style.opacity = "0.6";
        btnGuardarFirma.style.cursor = "not-allowed";

        signaturePadCliente.addEventListener(
          "endStroke",
          verificarTodoCompleto
        );
        signaturePadAsesor.addEventListener("endStroke", verificarTodoCompleto);

        btnLimpiarCliente.onclick = () => {
          signaturePadCliente.clear();
          restaurarGuia(
            signaturePadCliente,
            canvasCliente,
            "Firma del Cliente"
          );
          verificarTodoCompleto();
        };

        btnLimpiarAsesor.onclick = () => {
          signaturePadAsesor.clear();
          restaurarGuia(signaturePadAsesor, canvasAsesor, "Firma del Asesor");
          verificarTodoCompleto();
        };

        btnGuardarFirma.onclick = async function () {
          limpiarGuiaSinBorrarFirma(canvasCliente, signaturePadCliente);
          limpiarGuiaSinBorrarFirma(canvasAsesor, signaturePadAsesor);

          const firmaClienteBytes = await fetch(
            signaturePadCliente.toDataURL()
          ).then((res) => res.arrayBuffer());
          const firmaAsesorBytes = await fetch(
            signaturePadAsesor.toDataURL()
          ).then((res) => res.arrayBuffer());

          const firmaClienteImg = await pdfDoc.embedPng(firmaClienteBytes);
          const firmaAsesorImg = await pdfDoc.embedPng(firmaAsesorBytes);

          page.drawImage(firmaClienteImg, {
            x: 100,
            y: 125,
            width: 135,
            height: 65,
          });
          page.drawImage(firmaAsesorImg, {
            x: 100,
            y: 215,
            width: 135,
            height: 65,
          });

          const pdfFirmadoBytes = await pdfDoc.save();
          const pdfFirmadoBlob = new Blob([pdfFirmadoBytes], {
            type: "application/pdf",
          });

          // Crear File y agregarlo al estado
          const nombreCompleto =
            `Carta_Aceptacion_${nombreCliente || 'Cliente'}.pdf`;
          const pdfFile = new File([pdfFirmadoBlob], nombreCompleto, {
            type: 'application/pdf',
          });
          agregarArchivo(pdfFile);

          const pdfFirmadoUrl = URL.createObjectURL(pdfFirmadoBlob);
          nuevaVentana.document.body.innerHTML = `<embed src="${pdfFirmadoUrl}" width="100%" height="100%">`;

          setTimeout(() => {
            nuevaVentana.close();
            resolve(true);
          }, 500);
        };
      }, 500);
    });
  };

  const [accesoriosModalValue, setAccesoriosModalValue] = useState("0");
  const [financiamientoModalValue, setFinanciamientoModalValue] = useState("0");
  const [mostrarVideoIntro, setMostrarVideoIntro] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(apiUrlConsulta, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ access_token: accessToken, ...filters }),
        });

        const data = await response.json();
        console.log("Respuesta de la API:", data);

        if (data.statusCode === 200 && Array.isArray(data.data?.resultados)) {
          const processedData = data.data.resultados.map((cliente) => ({
            ...cliente,
          }));
          setMailData(processedData);
          console.log("Respuesta de PROCESSEDDATA:", processedData);

          // Buscar cliente por folioVenta
          if (folioVenta) {
            const clienteEncontrado = processedData.find(
              (cliente) => cliente.folio_venta === folioVenta
            );

            if (clienteEncontrado) {
              console.log("Cliente encontrado por folio:", clienteEncontrado);
              setSelectedMailData(clienteEncontrado);
              const nuevoValor = clienteEncontrado.accesorios_modal;
              const nuevoValorGraficas = clienteEncontrado.financiamiento_modal;

              if (nuevoValor !== accesoriosModalValue) {
                setAccesoriosModalValue(nuevoValor);

                if (nuevoValor === "1") {
                  setMostrarVideoIntro(true); // Mostrar el video introductorio

                  // Enviar al backend que ya se mostró
                  try {
                    await fetch(apiUrlUpdate, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        access_token: accessToken,
                        venta_id: clienteEncontrado.id,
                        accesorios_modal: "0",
                      }),
                    });

                    setAccesoriosModalValue("0"); // También actualizar en frontend
                  } catch (error) {
                    console.error(
                      "Error actualizando accesorios_modal:",
                      error
                    );
                  }
                } else {
                  setMostrarModalAccesorios(false);
                }
              }

              if (nuevoValorGraficas !== financiamientoModalValue) {
                setFinanciamientoModalValue(nuevoValorGraficas);

                if (nuevoValorGraficas === "1") {
                  handleShowModalEstadisticas(); // Mostrar el modal solo una vez

                  // Enviar al backend que ya se mostró
                  try {
                    await fetch(apiUrlUpdate, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        access_token: accessToken,
                        venta_id: clienteEncontrado.id,
                        financiamiento_modal: "0",
                      }),
                    });

                    setFinanciamientoModalValue("0"); // Reflejar también en el frontend
                  } catch (error) {
                    console.error(
                      "Error actualizando financiamiento_modal:",
                      error
                    );
                  }
                }
              }
            } else {
              console.warn("No se encontró cliente con ese folio.");
            }
          }
        }
      } catch (error) {
        console.error("Error al consultar la API:", error);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [accesoriosModalValue, accessToken, filters, folioVenta]);

  useEffect(() => {
    const actualizarFirmaModal = async () => {
      if (mostrarModalInfo && selectedMailData?.id) {
        try {
          await fetch(apiUrlUpdate, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              access_token: accessToken,
              venta_id: selectedMailData.id,
              firma_modal: "1",
            }),
          });

          console.log("firma_modal actualizado a 1 exitosamente.");
        } catch (error) {
          console.error("Error al actualizar firma_modal:", error);
        }
      }
    };

    actualizarFirmaModal();
  }, [mostrarModalInfo]);

  const cerrarModalAccesorios = async () => {
    //Manejo de modal accesorios
    try {
      setMostrarModalAccesorios(false); // Oculta el modal

      // Envía actualización a la API
      const response = await fetch(apiUrlUpdate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: accessToken,
          venta_id: selectedMailData?.id,
          accesorios_modal: "0",
        }),
      });

      const result = await response.json();
      console.log("Actualización enviada:", result);

      // Asegúrate de actualizar el valor local también
      if (result.statusCode === 200) {
        setAccesoriosModalValue("0");
      }
    } catch (error) {
      console.error("Error al actualizar accesorios_modal:", error);
    }
  };

  const [nombreCliente, setNombreCliente] = useState("");

  // Primer useEffect - Manejo del flujo principal de modales
  useEffect(() => {
    if (!folioVenta) return;

    const yaAceptoPrivacidad = localStorage.getItem(
      `avisoPrivacidadAceptado_${folioVenta}`
    );

    if (yaAceptoPrivacidad) {
      return;
    }

    // Mostrar formulario al inicio
    const formularioModalElement = document.getElementById("formularioModal");
    const formularioModal = new Modal(formularioModalElement);
    formularioModal.show();

    // Crear venta apenas se muestre el formulario
    const crearVenta = async () => {
      const ventaCreada = await generarVentaInicial();
      if (!ventaCreada) {
        console.warn("No se pudo crear la venta.");
        return;
      }
      console.log("Venta creada con éxito.");
    };
    crearVenta();

  }, [folioVenta]);

  // Segundo useEffect - Validación del formulario
  useEffect(() => {
    const nombre = document.getElementById("nombre");
    const telefono = document.getElementById("telefono");
    const correo = document.getElementById("correo");
    const btnEnviar = document.getElementById("btnEnviarFormulario");

    if (!nombre || !telefono || !correo || !btnEnviar) return;

    const validarFormulario = () => {
      const nombreValido = nombre.value.trim() !== "";
      const telefonoValido = telefono.value.trim().length >= 8;
      const correoValido = correo.value.includes("@");

      console.log({ nombreValido, telefonoValido, correoValido });

      btnEnviar.disabled = !(nombreValido && telefonoValido && correoValido);
    };

    // Agregar listeners
    nombre.addEventListener("input", validarFormulario);
    telefono.addEventListener("input", validarFormulario);
    correo.addEventListener("input", validarFormulario);

    // Validar al iniciar
    validarFormulario();

    // Cleanup
    return () => {
      nombre.removeEventListener("input", validarFormulario);
      telefono.removeEventListener("input", validarFormulario);
      correo.removeEventListener("input", validarFormulario);
    };
  }, []);

  // Tercer useEffect - Manejo del envío del formulario
  useEffect(() => {
    const btnEnviar = document.getElementById("btnEnviarFormulario");

    if (!btnEnviar) return;

    const handleEnviar = async () => {
      const nombre = document.getElementById("nombre").value.trim();
      const telefono = document.getElementById("telefono").value.trim();
      const correo_electronico = document.getElementById("correo").value.trim();

      // Validación final
      if (!nombre || telefono.length < 8 || !correo_electronico.includes("@")) {
        alert("Por favor completa correctamente todos los campos.");
        return;
      }

      const dataToSend = {
        access_token: accessToken,
        venta_id: selectedMailData?.id || folioVenta,
        nombre,
        telefono,
        correo_electronico,
      };

      console.log("Datos que se enviarán en la petición:", dataToSend);

      try {
        const response = await fetch(
          "https://api.checkmark.mx/actualizar/estado/financiamiento",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(dataToSend),
          }
        );

        const result = await response.json();

        if (result.statusCode === 200) {
          // Guardar el nombre para usarlo después
          setNombreCliente(nombre);
          localStorage.setItem(`nombreCliente_${folioVenta}`, nombre);

          console.log("Datos enviados correctamente");

          // AQUÍ ESTÁ EL CAMBIO PRINCIPAL: Después de enviar datos, mostrar aviso de privacidad
          mostrarAvisoPrivacidad(nombre);

          // Limpiar formulario
          document.getElementById("nombre").value = "";
          document.getElementById("telefono").value = "";
          document.getElementById("correo").value = "";
        } else {
          console.warn("Error al guardar datos", result);
          alert("Ocurrió un error al guardar la información.");
        }
      } catch (error) {
        console.error("Error en la petición:", error);
        alert("Error de conexión al guardar los datos.");
      }
    };

    btnEnviar.addEventListener("click", handleEnviar);

    return () => {
      btnEnviar.removeEventListener("click", handleEnviar);
    };
  }, [folioVenta, accessToken, selectedMailData]);

  // NUEVA FUNCIÓN: Manejar el flujo después del formulario
  const mostrarAvisoPrivacidad = (nombreGuardado) => {
    // Ocultar formulario
    const formularioModalElement = document.getElementById("formularioModal");
    const formularioModal = Modal.getInstance(formularioModalElement);
    formularioModal?.hide();

    // Esperar a que se oculte completamente y luego mostrar aviso de privacidad
    setTimeout(() => {
      const avisoModalElement = document.getElementById("avisoPrivacidadModal");
      const avisoModal = new Modal(avisoModalElement);
      avisoModal.show();

      const checkbox = document.getElementById("aceptoPrivacidad");
      const btnContinuar = document.getElementById("btnContinuarPrivacidad");

      // Resetear checkbox
      checkbox.checked = false;
      btnContinuar.disabled = true;

      const toggleButton = () => {
        btnContinuar.disabled = !checkbox.checked;
      };

      const handleAceptarAviso = () => {
        localStorage.setItem(`avisoPrivacidadAceptado_${folioVenta}`, "true");

        avisoModal.hide();

        // Mostrar modal de bienvenida después de aceptar privacidad
        setTimeout(() => {
          mostrarBienvenida(nombreGuardado);
        }, 300);
      };

      // Agregar event listeners
      checkbox.addEventListener("change", toggleButton);
      btnContinuar.addEventListener("click", handleAceptarAviso);

      // Cleanup cuando se cierre el modal
      avisoModalElement.addEventListener(
        "hidden.bs.modal",
        () => {
          checkbox.removeEventListener("change", toggleButton);
          btnContinuar.removeEventListener("click", handleAceptarAviso);
        },
        { once: true }
      );
    }, 300); // Dar tiempo a que se cierre el modal anterior
  };

  // NUEVA FUNCIÓN: Mostrar modal de bienvenida
  const mostrarBienvenida = (nombreGuardado) => {
    const bienvenidaMensajeElem = document.getElementById("bienvenidaMensaje");
    if (bienvenidaMensajeElem) {
      bienvenidaMensajeElem.textContent = `¡Bienvenido, ${nombreGuardado}! Gracias por confiar en nosotros.`;
    }

    const bienvenidaModalElement = document.getElementById("bienvenidaModal");
    const bienvenidaModal = new Modal(bienvenidaModalElement);
    bienvenidaModal.show();
  };

  // Función para generar venta inicial (sin cambios)
  const generarVentaInicial = async () => {
    const folioVenta = sessionStorage.getItem("folio_venta");
    if (!folioVenta) {
      console.error("No hay folio de venta disponible.");
      return false;
    }

    const ventaInicial = {
      folio_venta: folioVenta,
      ag_id: agenciaActual.ag_id,
      agencia: agenciaActual.nombre,
      access_token: accessToken,
      acepta_aviso_privacidad: "1",
    };

    console.log("Generando venta inicial:", ventaInicial);

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ventaInicial),
      });

      const result = await response.json();
      console.log("Respuesta al generar venta:", result);
      return response.ok;
    } catch (error) {
      console.error("Error al generar la venta:", error);
      return false;
    }
  };

  // Función para obtener vehículos con filtros
  const fetchVehicles = async (agencyName = "") => {
    if (initialLoad && !agencyName) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const currentCart = [...orderData];

    // Si no hay una agencia seleccionada, no hacemos la solicitud
    if (!agencyName) {
      console.warn("No se ha seleccionado una agencia");
      setLoading(false);
      return;
    }

    const agencia = agenciaInfo[agencyName] || {};
    const marca = agencia.marca || "";
    const ag_id = agencia.ag_id || "";

    // Verificar que tenemos filtros válidos
    if (!marca || !ag_id) {
      console.warn("Faltan filtros válidos: marca o ag_id");
      setLoading(false);
      return;
    }

    const requestData = {
      access_token: accessToken,
      marca,
      ag_id,
    };

    try {
      const response = await fetch(apiUrlInventario, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();
      console.log("Datos de la API :", data); //guarda todo en data

      if (data?.vehicles) {
        setTableData(data.vehicles); // Asegúrate de que los datos sean del tipo esperado
      }

      // Si no hay carrito previo, actualizarlo con los datos de la respuesta
      if (
        currentCart.length === 0 &&
        Array.isArray(data.order) &&
        data.order.length > 0
      ) {
        setOrderData(data.order);
        sessionStorage.setItem("orderCart", JSON.stringify(data.order));
      } else {
        setOrderData(currentCart);
      }

      setOrderHistoryData(data.orderHistory || []);
      setInitialLoad(false); // Ya no estamos en carga inicial
      setCategoryType("all");
    } catch (error) {
      console.error("Error al consumir la API:", error);
    } finally {
      setLoading(false);
    }
  };

  const [userModules, setUserModules] = useState(
    JSON.parse(localStorage.getItem("modulos")) || []
  );

  const [agenciaActual, setAgenciaActual] = useState({
    ag_id: null,
    nombre: "",
  });

  // Verificar admin
  const isAdmin = userModules.includes("admin");

  useEffect(() => {
    const agenciasStorage = JSON.parse(
      localStorage.getItem("agencias") || "[]"
    );

    // Si es admin, fuerza la agencia Renault Universidad
    if (isAdmin) {
      const nissanSantaClaraAgId = "ag0533_5598bb1d621ab";
      const nombre = "Renault Universidad";
      // Solo sobreescribe si no está ya como actual
      if (!agenciasStorage.includes(nissanSantaClaraAgId)) {
        localStorage.setItem(
          "agencias",
          JSON.stringify([nissanSantaClaraAgId])
        );
        setAgenciaActual({ ag_id: nissanSantaClaraAgId, nombre });
        fetchVehicles(nombre);
      } else {
        setAgenciaActual({ ag_id: nissanSantaClaraAgId, nombre });
        fetchVehicles(nombre);
      }
    } else {
      // Lógica para no admin
      if (agenciasStorage.length > 0) {
        const ag_id = agenciasStorage[0];
        const agenciaEncontrada = Object.entries(agenciaInfo).find(
          ([_, data]) => data.ag_id === ag_id
        );
        if (agenciaEncontrada) {
          const [nombre, data] = agenciaEncontrada;
          setAgenciaActual({ ag_id: data.ag_id, nombre });
          fetchVehicles(nombre);
        } else {
          setAgenciaActual({ ag_id: null, nombre: "" });
          console.warn("El ag_id no está en agenciaInfo:", ag_id);
        }
      } else {
        setAgenciaActual({ ag_id: null, nombre: "" });
        console.warn("No hay agencias guardadas en localStorage");
      }
    }
    // Solo depende de isAdmin y NO repite si cambia fetchVehicles
    // eslint-disable-next-line
  }, [isAdmin]);


  useEffect(() => {
    let folio = sessionStorage.getItem("folio_venta");

    if (!folio) {
      folio = generateFolio();
      sessionStorage.setItem("folio_venta", folio);
    }

    setFolioVenta(folio);
  }, []);

  const sendOrder = async () => {
    console.log("Finalizando compra...");

    if (!orderData || orderData.length === 0) {
      Swal.fire("Error", "No hay información de la orden", "error");
      return;
    }

    const folioVenta = sessionStorage.getItem("folio_venta");
    if (!folioVenta) {
      Swal.fire("Error", "No se pudo obtener el folio de venta.", "error");
      return;
    }

    // En este punto, todos los datos ya deberían estar en el servidor
    // Podemos enviar una actualización final del precio total si es necesario
    const finalData = {
      folio_venta: folioVenta,
      access_token: accessToken,
      precio_total: getTotalPrice(),
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalData),
      });

      const result = await response.json();
      console.log("Respuesta del servidor (finalización):", result);

      if (response.ok) {
        setIsDisabled(true);
        Swal.fire("Éxito", "Orden finalizada con éxito", "success");
        // Aquí puedes redirigir al usuario a la página de confirmación
      } else {
        Swal.fire("Error", "Error al finalizar la orden", "error");
      }
    } catch (error) {
      console.error("Error en la petición:", error);
      Swal.fire("Error", "Hubo un problema al finalizar la orden.", "error");
    }
  };

  const fetchVehicleData = async () => {
    try {
      const apiUrlVehicle =
        "https://api.checkmark.mx/obtener/venta/financiamiento";
      const folioVenta = sessionStorage.getItem("folio_venta");

      const response = await fetch(apiUrlVehicle, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: accessToken,
          folio_venta: folioVenta,
        }),
      });

      const data = await response.json();
      if (data.statusCode === 200 && data.data?.resultados?.[0]) {
        const processedData = data.data.resultados.map((cliente) => {
          let productos = [];
          let planesSeleccionados = [];
          let planesFinanciamiento = [];

          // Procesar accesorios seleccionados (plan_accesorios)
          if (
            cliente.accesorios_seleccionados &&
            cliente.accesorios_seleccionados !== '"null"'
          ) {
            let accesorioString;

            try {
              accesorioString = JSON.parse(cliente.accesorios_seleccionados);
            } catch {
              accesorioString = cliente.accesorios_seleccionados;
            }

            if (typeof accesorioString === "string") {
              try {
                accesorioString = JSON.parse(accesorioString);
              } catch {
                accesorioString = [];
              }
            }

            productos = Array.isArray(accesorioString)
              ? accesorioString
              : [accesorioString];
          }

          // Procesar planes seleccionados
          if (
            cliente.planes_seleccionados &&
            cliente.planes_seleccionados !== '"null"'
          ) {
            let planesString;

            try {
              planesString = JSON.parse(cliente.planes_seleccionados);
            } catch {
              planesString = cliente.planes_seleccionados;
            }

            if (typeof planesString === "string") {
              try {
                planesString = JSON.parse(planesString);
              } catch {
                planesString = [];
              }
            }

            planesSeleccionados = Array.isArray(planesString)
              ? planesString
              : [planesString];
          }

          // Procesar planes de financiamiento
          if (
            cliente.planes_financiamiento &&
            cliente.planes_financiamiento !== '"null"'
          ) {
            let planesFinanciamientoString;

            try {
              planesFinanciamientoString = JSON.parse(
                cliente.planes_financiamiento
              );
            } catch {
              planesFinanciamientoString = cliente.planes_financiamiento;
            }

            if (typeof planesFinanciamientoString === "string") {
              try {
                planesFinanciamientoString = JSON.parse(
                  planesFinanciamientoString
                );
              } catch {
                planesFinanciamientoString = [];
              }
            }

            planesFinanciamiento = Array.isArray(planesFinanciamientoString)
              ? planesFinanciamientoString
              : [planesFinanciamientoString];
          }

          return {
            ...cliente,
            productos, // Accesorios procesados
            planesSeleccionados, // Planes seleccionados
            planesFinanciamiento, // Planes de financiamiento
          };
        });

        setVehicleData(processedData[0] || null);
      } else {
        console.error("No hay registros disponibles.");
        setVehicleData(null);
      }
    } catch (err) {
      console.error("Error al obtener los datos:", err);
      setVehicleData(null);
    }
  };

  useEffect(() => {
    fetchVehicleData();
    const interval = setInterval(fetchVehicleData, 3000);
    return () => clearInterval(interval);
  }, []);

  // Context and general state
  const context = useContext(AppSettings);
  const [posMobileSidebarToggled, setPosMobileSidebarToggled] = useState(false);
  const [categoryType, setCategoryType] = useState("all");
  const [tableData, setTableData] = useState([]);
  const [orderData, setOrderData] = useState(() => {
    try {
      // Intentar recuperar el carrito guardado en sessionStorage
      const savedCart = sessionStorage.getItem("orderCart");
      // Si existe, devolver el carrito parseado, sino array vacío
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error("Error al cargar el carrito desde sessionStorage:", error);
      return [];
    }
  });
  const [orderHistoryData, setOrderHistoryData] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const updateOrderDataAndSave = (newOrderData) => {
    setOrderData(newOrderData);
    sessionStorage.setItem("orderCart", JSON.stringify(newOrderData));
  };

  // Phone validation states
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [phoneModalInstance, setPhoneModalInstance] = useState(null);

  // Product modal states
  const [productModal, setProductModal] = useState();
  const [modalData, setModalData] = useState();
  const [modalQuantity, setModalQuantity] = useState();
  const [modalSelectedSize, setModalSelectedSize] = useState();
  const [modalSelectedAddon, setModalSelectedAddon] = useState([]);

  // Original POS functions
  var toggleMobileSidebar = (event) => {
    event.preventDefault();
    setPosMobileSidebarToggled(true);
  };

  var dismissMobileSidebar = (event) => {
    event.preventDefault();
    setPosMobileSidebarToggled(false);
  };

  var showType = (event, type) => {
    event.preventDefault();

    if (tableData && tableData.food) {
      for (var i = 0; i < tableData.food.length; i++) {
        if (tableData.food[i].type === type || type === "all") {
          tableData.food[i].hide = false;
        } else {
          tableData.food[i].hide = true;
        }
      }

      setTableData(tableData);
      setCategoryType(type);
    }
  };

  // Función para enviar un vehículo (nuevo o seminuevo) al servidor
  const sendVehicleToServer = async (vehicleData) => {
    const folioVenta = sessionStorage.getItem("folio_venta");
    if (!folioVenta) {
      console.error("No hay folio de venta disponible");
      return false;
    }

    const allOptions = vehicleData.availableOptions;

    const orderDataItem = {
      folio_venta: folioVenta,
      marca_vehiculo: vehicleData.title,
      modelo_vehiculo: vehicleData.description,
      ano_vehiculo: vehicleData.year,
      url_img: vehicleData.image,
      access_token: accessToken,
      price: vehicleData.price,
      type: vehicleData.type,
      precio_total: getTotalPrice(),
      ag_id: agenciaActual.ag_id,
      agencia: agenciaActual.nombre,
      versiones: allOptions,
    };

    console.log("Enviando datos del vehículo:", orderDataItem);

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderDataItem),
      });

      const result = await response.json();
      console.log("Respuesta del servidor (vehículo):", result);
      return response.ok;
    } catch (error) {
      console.error("Error al enviar el vehículo:", error);
      return false;
    }
  };

  // Función para enviar un accesorio o garantía al servidor
  const sendAccessoryToServer = async (accessoryData) => {
    const folioVenta = sessionStorage.getItem("folio_venta");
    if (!folioVenta) {
      console.error("No hay folio de venta disponible");
      return false;
    }

    // Simplificamos el accesorio para que solo tenga title y price
    const accessoryItem = {
      title: accessoryData.title,
      price: accessoryData.price.toString(), // Convertimos a string para mantener formato compatible
      description: accessoryData.description, //mandamos descripción también
    };

    const orderDataItem = {
      folio_venta: folioVenta,
      access_token: accessToken,
      nuevo_accesorio: accessoryItem,
      precio_total: getTotalPrice(), // Calculamos el precio total actual
    };

    console.log("Enviando datos del accesorio:", orderDataItem);

    try {
      const response = await fetch(`${apiUrl}/accesorios`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderDataItem),
      });

      const result = await response.json();
      console.log("Respuesta del servidor (accesorio):", result);
      return response.ok;
    } catch (error) {
      console.error("Error al enviar el accesorio:", error);
      return false;
    }
  };

  // Modificación de la función addToCartDirectly para accesorios/garantías
  const addToCartDirectly = async (food) => {
    // Si el producto es accesorio o garantía, actualizamos también el selectedBrand
    if (food.type === "accesorios" || food.type === "garantias") {
      // Actualizar selectedBrand antes de agregar el producto al carrito
      setSelectedBrand(food.brand);

      // Preparar objeto del nuevo accesorio/garantía
      const newAccessoryData = {
        id: food.id,
        image: food.image,
        title: food.title,
        price: food.price,
        year: food.year,
        type: food.type,
        description: food.modelo,
        quantity: 1,
        options: [],
      };

      // Enviar al servidor primero
      const serverResult = await sendAccessoryToServer(newAccessoryData);

      if (!serverResult) {
        Swal.fire(
          "Error",
          "No se pudo guardar el accesorio en el servidor",
          "error"
        );
        // Continuar de todos modos con la UI para no interrumpir la experiencia del usuario
      }
    }

    // Agregamos el producto al carrito y guardamos en sessionStorage
    const newOrderData = [
      ...orderData,
      {
        id: food.id,
        image: food.image,
        title: food.title,
        price: food.price,
        year: food.year,
        type: food.type,
        description: food.modelo,
        quantity: 1, // Se agrega 1 por defecto
        options: [], // Sin opciones
      },
    ];

    updateOrderDataAndSave(newOrderData);
  };

  // Verificar si ya está agregado el accesorio/garantia
  const isInCart = (producto) => {
    return orderData.some((item) => item.id === producto.id);
  };

  var showPosItemModal = (event, food) => {
    event.preventDefault();

    // Guardamos la marca del vehículo seleccionado
    setSelectedBrand(food.brand);

    // Abrimos el modal con la información del producto
    setModalData(food);
    setModalQuantity(1);
    productModal.show();
  };

  const showAccessoryModal = (food) => {
    setAccessoryModalData(food);
    setAccessoryModalVisible(true);
  };

  var getOrderTotal = () => {
    return orderData ? orderData.length : 0;
  };

  var getTotalPrice = () => {
    var value = 0;

    if (orderData) {
      for (var i = 0; i < orderData.length; i++) {
        value +=
          parseFloat(orderData[i].price) * parseInt(orderData[i].quantity);
      }
    }
    return Math.round(value); // Redondeamos para obtener un número entero
  };

  var toggleConfirmation = (event, id, value) => {
    event.preventDefault();

    if (orderData) {
      const newData = orderData.map((obj) => {
        if (obj.id === id) {
          return { ...obj, confirmation: value };
        }
        return obj;
      });

      setOrderData(newData);
    }
  };

  var removeOrder = (event, id) => {
    event.preventDefault();

    if (orderData) {
      const newData = orderData.filter(function (order) {
        return order.id !== id;
      });

      setOrderData(newData);
    }
  };

  // Modificación de la función addToCart para vehículos
  const addToCart = async (event) => {
    event.preventDefault();
    productModal.hide();

    var options = [];
    let finalPrice = modalData.price;

    // No seleccionamos una versión específica, solo usamos el precio base

    // Agregar addons si existen
    if (modalSelectedAddon) {
      for (var i = 0; i < modalSelectedAddon.length; i++) {
        options.push({
          key: "addon",
          value: modalSelectedAddon[i],
        });
      }
    }

    setTimeout(async () => {
      let updatedOrderData = [...orderData];

      // Preparar objeto del nuevo vehículo
      const newVehicleData = {
        id: modalData.id,
        image: modalData.image,
        title: modalData.title,
        price: finalPrice,
        year: modalData.year,
        type: modalData.type,
        description: modalData.description,
        quantity: modalQuantity,
        options: options,
        // Agregar las opciones completas para tenerlas disponibles
        availableOptions: modalData.options ? modalData.options : {},
      };

      // Reemplazar si es tipo "nuevo" o "seminuevo"
      if (modalData.type === "nuevos" || modalData.type === "seminuevos") {
        updatedOrderData = updatedOrderData.filter(
          (item) => item.type !== "nuevos" && item.type !== "seminuevos"
        );

        // Guardar el precio base del vehículo
        setPrecioBaseVehiculo(finalPrice);

        // Enviar al servidor antes de actualizar el estado local
        const serverResult = await sendVehicleToServer(newVehicleData);

        if (!serverResult) {
          Swal.fire(
            "Error",
            "No se pudo guardar el vehículo en el servidor",
            "error"
          );
          // Continuar de todos modos con la UI para no interrumpir la experiencia del usuario
        }
      }

      updatedOrderData.push(newVehicleData);
      updateOrderDataAndSave(updatedOrderData);
    }, 500);
  };

  useEffect(() => {
    if (vehicleData?.planesFinanciamiento) {
      const seleccionado = vehicleData.planesFinanciamiento.find(p => p.isSelected);
      if (seleccionado) {
        setPlanSeleccionado(seleccionado);
      }
    }
  }, [vehicleData]);

  useEffect(() => {
    // Verifica si el valor de selectedBrand ha cambiado y aplica el filtro
    console.log("selectedBrand:", selectedBrand);
  }, [selectedBrand]); // Se ejecutará cada vez que selectedBrand cambie

  useEffect(() => {
    // Configuración inicial del contexto
    context.setAppHeaderNone(true);
    context.setAppSidebarNone(true);
    context.setAppContentFullHeight(true);
    context.setAppContentClass("p-0");

    // Creación del modal del producto
    setProductModal(new Modal(document.getElementById("modalPosItem")));

    // Primero, cargar el carrito desde sessionStorage (esto debe hacerse antes de la API)
    const savedCart = sessionStorage.getItem("orderCart");
    const initialCart = savedCart ? JSON.parse(savedCart) : [];

    if (initialCart && initialCart.length > 0) {
      setOrderData(initialCart);
    }

    // --- Aquí empieza lo importante ---
    const agenciasStorage = JSON.parse(
      localStorage.getItem("agencias") || "[]"
    );
    // Obtén los módulos del usuario
    const userModules = JSON.parse(localStorage.getItem("modulos") || "[]");
    const isAdmin = userModules.includes("admin");

    let ag_id;
    let nombre;

    if (isAdmin) {
      // Forzar Renault Universidad
      ag_id = "ag0533_5598bb1d621ab";
      nombre = "Renault Universidad";
    } else if (agenciasStorage.length > 0) {
      ag_id = agenciasStorage[0];
      // Buscar agencia con ag_id en agenciaInfo
      const agenciaEncontrada = Object.entries(agenciaInfo).find(
        ([_, data]) => data.ag_id === ag_id
      );
      if (agenciaEncontrada) {
        nombre = agenciaEncontrada[0];
      }
    }

    if (ag_id && nombre) {
      console.log("Agencia usada para inventario:", nombre);

      const agencia = agenciaInfo[nombre] || {};
      const marca = agencia.marca || "";
      const requestData = {
        access_token: accessToken,
        marca,
        ag_id,
      };

      fetch(apiUrlInventario, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then((res) => res.json())
        .then((data) => {
          setTableData(data);

          if (
            (!initialCart || initialCart.length === 0) &&
            Array.isArray(data.order) &&
            data.order.length > 0
          ) {
            setOrderData(data.order);
            sessionStorage.setItem("orderCart", JSON.stringify(data.order));
          }
          setOrderHistoryData(data.orderHistory || []);
        })
        .catch((error) => {
          console.error("Error al consumir la API:", error);
        });
    } else {
      console.warn(
        "No hay agencias guardadas en localStorage o no se encontró la agencia para el admin."
      );
    }

    // Función de limpieza al desmontar el componente
    return () => {
      context.setAppHeaderNone(false);
      context.setAppSidebarNone(false);
      context.setAppContentFullHeight(false);
      context.setAppContentClass("");
    };
  }, []);

  // Configuración inicial
  useEffect(() => {
    // Configuración inicial del contexto
    context.setAppHeaderNone(true);
    context.setAppSidebarNone(true);
    context.setAppContentFullHeight(true);
    context.setAppContentClass("p-0");

    // Creación del modal del producto
    setProductModal(new Modal(document.getElementById("modalPosItem")));

    // Ya no hacemos la carga inicial automática de vehículos
    // fetchVehicles(); <-- Esta línea se elimina

    // Función de limpieza al desmontar el componente
    return () => {
      context.setAppHeaderNone(false);
      context.setAppSidebarNone(false);
      context.setAppContentFullHeight(false);
      context.setAppContentClass("");
    };
  }, []);

  const [selectedCar, setSelectedCar] = useState(null);
  const [mostrarModalAccesorios, setMostrarModalAccesorios] = useState(false);
  const [accesoriosGarantias, setAccesoriosGarantias] = React.useState({
    accesorios: [],
    garantias: [],
  });
  const [canvasActivo, setCanvasActivo] = useState(false);
  const canvasRef = useRef();
  const [busqueda, setBusqueda] = useState(""); //buscador de accesorios
  const [searchVehicle, setsearchVehicle] = useState(""); //buscador de vehiculos
  // Nuevo estado para controlar si el botón está activo
  const [isActive, setIsActive] = useState(true);

  const accesoriosFiltrados = accesoriosGarantias.accesorios.filter(
    (
      item //filtro para accesorios
    ) => item.title.toLowerCase().includes(busqueda.toLowerCase())
  );

  const garantiasFiltradas = accesoriosGarantias.garantias.filter(
    (
      item //filtro para garantias
    ) => item.title.toLowerCase().includes(busqueda.toLowerCase())
  );

  const actualizarFirmaModal = async (valor) => {
    if (!selectedMailData?.id) return; // Si no hay id, no hace nada

    try {
      await fetch(apiUrlUpdate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_token: accessToken,
          venta_id: selectedMailData.id,
          firma_modal: valor, // valor dinámico que recibimos
        }),
      });

      console.log(`firma_modal actualizado a ${valor} exitosamente.`);
    } catch (error) {
      console.error("Error al actualizar firma_modal:", error);
    }
  };

  const handleShowModalEstadisticas = () => {
    const modalElement = document.getElementById("accessoryModal");
    const modal = new Modal(modalElement);
    modal.show();
  };

  const handleCloseModalFinanciamiento = () => {
    const modalElement = document.getElementById("accessoryModal");
    const modal = Modal.getInstance(modalElement);
    if (modal) {
      modal.hide();
    }
  };

  const RibbonDiagonal = (
    { text, color } //etiqueta accesorios
  ) => (
    <div
      style={{
        position: "absolute",
        top: "28px",
        left: "-40px",
        backgroundColor: color,
        color: "white",
        padding: "5px 29px",
        transform: "rotate(-45deg)",
        fontWeight: "bold",
        fontSize: "14px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
        zIndex: 10,
        borderRadius: "5px",
      }}
    >
      {text}
    </div>
  );

  React.useEffect(() => {
    if (tableData?.food) {
      setAccesoriosGarantias({
        accesorios: tableData.food.filter(
          (item) => item.type === "accesorios" && !item.hide
        ),
        garantias: tableData.food.filter(
          (item) => item.type === "garantias" && !item.hide
        ),
      });
    }
  }, [tableData]);

  const renderLineLabel = ({ x, y, value }) => {
    return (
      <text
        x={x}
        y={y + 35}
        fontSize={11}
        fill="var(--label-color)"
        textAnchor="middle"
      >
        {`${value}%`}
      </text>
    );
  };

  const parsedSelectedPlans = useMemo(() => {
    //mostrar el pna seleccionado (modo espejo)
    try {
      return JSON.parse(
        JSON.parse(selectedMailData?.planes_seleccionados || "[]")
      );
    } catch (e) {
      return [];
    }
  }, [selectedMailData]);

  const parsedAccesoriosSeleccionados = useMemo(() => {
    try {
      return JSON.parse(selectedMailData?.accesorios_seleccionados || "[]");
    } catch (e) {
      return [];
    }
  }, [selectedMailData]);

  /************* FICHAS TECNICAS *************/
  const [openFicha, setOpenFicha] = useState(false);
  const [fichaURL, setFichaURL] = useState("");

  const clean = (str) => //limpiar strings
    encodeURIComponent(
      str.trim().toLowerCase().replace(/\s+/g, "_")
    );

  const abrirPDF = () => {
    const marca = selectedCar.title.toLowerCase();
    const url = `/assets/fichas/${marca}/${clean(selectedCar.description)}.pdf`;
    setFichaURL(url);      // guardamos la ruta
    setOpenFicha(true);    // mostramos el modal
  };

  useEffect(() => {
    if (selectedCar && !openFicha) {
      abrirPDF();
    }
  }, [selectedCar]);

  useEffect(() => {
    if (selectedCar && openFicha) {
      abrirPDF();
    }
  }, [openFicha]);


  return (
    <>
      {/* Modal de Bienvenida */}
      <div
        id="bienvenidaModal"
        className="modal fade"
        tabIndex="-1"
        aria-labelledby="bienvenidaTitulo"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content text-center">
            <div className="modal-body">
              <div className="mb-3">
                <i className="bi bi-emoji-smile"></i>
              </div>
              <h5 className="modal-title" id="bienvenidaTitulo">
                ¡Bienvenido!
              </h5>
              <p id="bienvenidaMensaje">
                Estamos encantados de tenerte con nosotros.
              </p>
              <button
                type="button"
                className="btn btn-success"
                data-bs-dismiss="modal"
              >
                Comenzar
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Modal de Aviso de Privacidad */}
      <div
        className="modal fade"
        id="avisoPrivacidadModal"
        tabIndex="-1"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Aviso de Privacidad</h5>
            </div>
            <div className="modal-body text-white">
              <p>
                Este sistema utiliza información personal para brindar un mejor
                servicio. Al continuar, acepta nuestro aviso de privacidad.
              </p>

              {/* Enlace para ver el PDF */}
              <p>
                <a
                  href="/simulacion_aviso.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-link text-info p-0"
                >
                  Ver aviso de privacidad completo
                </a>
              </p>

              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="aceptoPrivacidad"
                />
                <label className="form-check-label" htmlFor="aceptoPrivacidad">
                  He leído y acepto el aviso de privacidad.
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                id="btnContinuarPrivacidad"
                className="btn btn-primary"
                disabled
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal - Formulario */}
      <div
        className="modal fade"
        id="formularioModal"
        tabIndex="-1"
        aria-hidden="true"
        data-bs-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Información personal</h5>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="nombre" className="form-label">
                  Nombre
                </label>
                <input
                  type="text"
                  id="nombre"
                  className="form-control"
                  placeholder="Ej. Juan Pérez"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="telefono" className="form-label">
                  Número de teléfono
                </label>
                <input
                  type="tel"
                  id="telefono"
                  className="form-control"
                  placeholder="Ej. 5551234567"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="correo" className="form-label">
                  Correo electrónico
                </label>
                <input
                  type="email"
                  id="correo"
                  className="form-control"
                  placeholder="Ej. juan@example.com"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                id="btnEnviarFormulario"
                className="btn btn-success"
                disabled
              >
                Enviar
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={
          "pos pos-with-menu pos-with-sidebar" +
          (posMobileSidebarToggled ? "pos-mobile-sidebar-toggled" : "")
        }
        id="pos"
      >
        <div className="pos-container">
          <div className="pos-menu">
            <div className="logo">
              <Link to="/analytics">
                <div className="logo-img">
                  <Icon
                    icon="material-symbols-light:home"
                    className="fs-50px text-white"
                  />
                </div>
                <div className="logo-text">INICIO</div>
              </Link>
            </div>
            <div className="nav-container">
              <PerfectScrollbar className="h-100">
                <ul className="nav nav-tabs">
                  {Array.isArray(tableData?.category)
                    ? tableData.category
                      .filter(
                        (category) =>
                          category.type !== "garantias" &&
                          category.type !== "accesorios"
                      )
                      .map((category, index) => (
                        <li className="nav-item" key={index}>
                          <a
                            className={
                              "nav-link" +
                              (category.type === categoryType
                                ? " active"
                                : "")
                            }
                            onClick={(event) => {
                              event.preventDefault(); // para evitar el salto al href
                              if (category.type === "accesorios") {
                                setMostrarModalAccesorios(true); //aqui abre el modal
                              } else {
                                showType(event, category.type); // comportamiento normal
                              }
                            }}
                            href="#/"
                          >
                            <Icon icon={category.icon} className="nav-icon" />{" "}
                            {category.text}
                          </a>
                        </li>
                      ))
                    : null}
                </ul>
              </PerfectScrollbar>
            </div>
          </div>

          <div className="pos-content">
            <PerfectScrollbar className="pos-content-container h-100">
              <div ref={planesRef} className="position-relative">
                {" "}
                {/* CANVAS */}
                {canvasActivo && (
                  <CanvasOverlay ref={canvasRef} containerRef={planesRef} />
                )}
                {/* Contenido con estado de carga */}
                {loading ? (
                  <div className="loader-data">
                    <Circles height="80" width="80" color="#4fa94d" />
                  </div>
                ) : initialLoad ? (
                  /* Mensaje inicial cuando no se ha seleccionado ninguna agencia */
                  <div className="text-center p-5">
                    <h4>Selecciona una agencia para ver su inventario</h4>
                    <p className="text-muted">
                      El inventario se cargará después de seleccionar una
                      agencia
                    </p>
                  </div>
                ) : selectedCar ? (
                  <div className="d-flex justify-content-center align-items-center p-4">
                    <div
                      className="card shadow-lg border-0"
                      style={{
                        maxWidth: "900px",
                        width: "100%",
                        borderRadius: "16px",
                        overflow: "visible",
                      }}
                    >
                      {/* Imagen principal con precio */}
                      <div className="position-relative">
                        <ZoomImage
                          src={selectedCar.image}
                          alt={selectedCar.title}
                          style={{
                            maxHeight: "480px",
                            borderRadius: "8px",
                          }}
                          zoom={2.5}
                        />
                        <span className="position-absolute text-price btn-zoom ">
                          Desde:{" "}
                          {vehicleData?.precio_vehiculo
                            ? formatCurrency(vehicleData.precio_vehiculo)
                            : "N/A"}
                        </span>
                        <button
                          className=" position-absolute btn btn-success btn-sm btn-fichas"
                          onClick={() => abrirPDF()} // generarPDFcontado()
                        >
                          Ficha Técnica
                        </button>
                      </div>

                      {/* Contenido principal */}
                      <div className="p-4">
                        <h3 className="fw-bold mb-1">{selectedCar.title}</h3>
                        <p className="text-muted">{selectedCar.modelo}</p>

                        {/*Botón*/}
                        <div className="d-flex mt-4">
                          <button
                            className="btn btn-success btn-lg w-100"
                            onClick={() => setSelectedCar(null)}
                            disabled={!isActive}
                          >
                            Volver al inventario
                          </button>
                        </div>
                      </div>

                      {/* NUEVA SECCIÓN: Resumen y planes debajo */}
                      <div className="mt-2 p-4">
                        <div className="row">
                          <h5 className="fw-bold mb-3">RESUMEN</h5>
                          <div className="row">
                            <div className="col-md-6"> {/* primera columna */}
                              <ul className="list-group list-group-flush fs-6">
                                <li className="list-group-item">
                                  <i className="fas fa-car me-2" style={{ color: "#00e58e" }}></i>
                                  Marca: {selectedCar.title || "N/A"}
                                </li>
                                <li className="list-group-item">
                                  <i className="fas fa-cube me-2" style={{ color: "#00e58e" }}></i>
                                  Modelo: {selectedCar.description || "N/A"}
                                </li>
                                <li className="list-group-item">
                                  <i className="fas fa-calendar me-2" style={{ color: "#00e58e" }}></i>
                                  Año: {selectedCar.year || "N/A"}
                                </li>
                                <li className="list-group-item">
                                  <i className="fas fa-cogs me-2" style={{ color: "#00e58e" }}></i>
                                  Versión: {selectedMailData?.version_vehiculo || "N/A"}
                                </li>
                                {/*se muestra si es credito no-contado*/}
                                {selectedMailData?.depende_auto === "No" && ( /* cambiar a MailData [selectedMailData?.depende_auto === "no"]*/
                                  <>
                                    <li className="list-group-item">
                                      <i className="fas fa-building me-2" style={{ color: "#00e58e" }}></i>
                                      Agencia: {vehicleData?.agencia || "N/A"}
                                    </li>
                                  </>
                                )}
                                <li className="list-group-item">
                                  <i className="fas fa-info-circle me-2" style={{ color: "#00e58e" }}></i>
                                  *El precio total mostrado no incluye accesorios ni garantías adicionales.
                                </li>
                              </ul>
                            </div>

                            <div className="col-md-6"> {/* segunda columna */}
                              <ul className="list-group list-group-flush fs-6">
                                {selectedMailData?.depende_auto === "Si" ? ( //si /* cambiar a MailData [selectedMailData?.depende_auto === "si"]*/
                                  /********* CONTADO ********/
                                  <>
                                    <li className="list-group-item">
                                      <i className="fas fa-calendar-alt me-2" style={{ color: "#00e58e" }}></i>
                                      Meses:{" "}
                                      {vehicleData?.meses_financiamiento > 0
                                        ? `${vehicleData.meses_financiamiento} meses`
                                        : "N/A"}
                                    </li>

                                    <li className="list-group-item">
                                      <i className="fas fa-dollar-sign me-2" style={{ color: "#00e58e" }}></i>
                                      Mensualidad:{" "}
                                      {vehicleData?.precio_total &&
                                        vehicleData?.planesFinanciamiento?.some(p => p.isSelected)
                                        ? `$${new Intl.NumberFormat("es-MX", {
                                          style: "decimal",
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        }).format(
                                          vehicleData.mensualidad_estimacion ??
                                          ((vehicleData.precio_total || 0) *
                                            (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                                          (vehicleData.meses_financiamiento || 12)
                                        )}`
                                        : "N/A"}
                                    </li>
                                    <li className="list-group-item">
                                      <i className="fas fa-money-bill-wave me-2" style={{ color: "#00e58e" }}></i>
                                      Precio Total*:{" "}
                                      {vehicleData?.precio_vehiculo
                                        ? formatCurrency(vehicleData.precio_vehiculo)
                                        : "N/A"}
                                    </li>

                                    {/*agencia se muestra aquí en contado */}
                                    <li className="list-group-item">
                                      <i className="fas fa-building me-2" style={{ color: "#00e58e" }}></i>
                                      Agencia: {vehicleData?.agencia || "N/A"}
                                    </li>
                                  </>
                                ) : (
                                  /********** CRÉDITO *********/
                                  <>
                                    <li className="list-group-item">
                                      <i className="fas fa-calendar-alt me-2" style={{ color: "#00e58e" }}></i>
                                      Meses:{" "}
                                      {vehicleData?.meses_financiamiento > 0
                                        ? `${vehicleData.meses_financiamiento} meses`
                                        : "N/A"}
                                    </li>

                                    <li className="list-group-item">
                                      <i className="fas fa-dollar-sign me-2" style={{ color: "#00e58e" }}></i>
                                      Mensualidad:{" "}
                                      {vehicleData?.precio_total &&
                                        vehicleData?.planesFinanciamiento?.some(p => p.isSelected)
                                        ? `$${new Intl.NumberFormat("es-MX", {
                                          style: "decimal",
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        }).format(
                                          ((vehicleData.precio_total || 0) *
                                            (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                                          (vehicleData.meses_financiamiento || 12)
                                        )}`
                                        : "N/A"}
                                    </li>

                                    <li className="list-group-item">
                                      <i className="fas fa-money-bill-wave me-2" style={{ color: "#00e58e" }}></i>
                                      Precio Total*:{" "}
                                      {vehicleData?.precio_vehiculo
                                        ? formatCurrency(vehicleData.precio_vehiculo)
                                        : "N/A"}
                                    </li>

                                    <li className="list-group-item">
                                      <i className="fas fa-coins me-2" style={{ color: "#00e58e" }}></i>
                                      Enganche:{" "}
                                      {selectedMailData?.enganche
                                        ? formatCurrency(selectedMailData.enganche)
                                        : "N/A"}
                                    </li>

                                    <li className="list-group-item">
                                      <div className="col-md-6 mb-3 text-center">
                                        <label className="fw-bold">
                                          <i className="fas fa-percentage me-2" style={{ color: "#00e58e" }}></i>
                                          Porcentaje de Enganche:
                                        </label>
                                        <input
                                          type="range"
                                          className="form-range"
                                          min="0"
                                          max="100"
                                          value={selectedMailData?.porcentaje_enganche || 0}
                                          readOnly
                                        />
                                        <span
                                          className="badge"
                                          style={{
                                            backgroundColor: "#00e58e",
                                            color: "#fff",
                                            fontSize: "0.65rem",
                                            padding: "0.35em 0.75em",
                                            borderRadius: "0.4rem",
                                          }}
                                        >
                                          {vehicleData?.porcentaje_enganche || 0}%
                                        </span>
                                      </div>
                                    </li>
                                  </>
                                )}
                              </ul>
                            </div>
                          </div>

                          <div className="col-12 mt-5">
                            <h5 className="fw-bold mb-3">PLANES</h5>
                            <div className="h-100">
                              <div className="h-100 align-items-center justify-content-center text-center">
                                <div className="row">
                                  {/* Iteramos sobre los 3 planes de financiamiento */}
                                  {vehicleData?.planesFinanciamiento?.some((plan) => plan.isSelected) && vehicleData?.planesFinanciamiento?.map(
                                    (plan, index) => {
                                      const precioVehiculo = parseFloat(
                                        vehicleData?.precio_vehiculo || 0
                                      );
                                      const enganchePorcentaje = parseFloat(
                                        selectedMailData?.porcentaje_enganche ||
                                        0
                                      );
                                      const totalAccesorios = parseFloat(
                                        plan?.totalPrice || 0
                                      );

                                      const totalPlan =
                                        precioVehiculo + totalAccesorios;
                                      const enganche =
                                        (enganchePorcentaje / 100) * totalPlan;
                                      const montoFinanciado =
                                        totalPlan - enganche;

                                      const mensualidad36 = Math.round(
                                        montoFinanciado / 36
                                      );
                                      const mensualidad48 = Math.round(
                                        montoFinanciado / 48
                                      );

                                      return (
                                        <div
                                          className="col-md-6 mb-4"
                                          key={index}
                                        >
                                          <div
                                            className={`card card-plans shadow-sm text-white ${plan.isSelected
                                              ? "border border-3 border-success bg-dark bg-opacity-75"
                                              : "bg-dark bg-opacity-50"
                                              }`}
                                          >
                                            <div
                                              className="card-body text-center"
                                              style={{
                                                overflow: "visible",
                                                position: "relative",
                                              }}
                                            >
                                              <h5 className="card-title text-uppercase mb-3">
                                                {plan.title}
                                              </h5>
                                              <h6 className="card-subtitle mb-2 text-muted">
                                                Accesorios incluidos:
                                              </h6>

                                              {plan.accessories &&
                                                plan.accessories.length > 0 ? (
                                                <div className="accessories-wrapper position-relative d-flex flex-wrap justify-content-center mt-2">
                                                  {plan.accessories.map(
                                                    (accesorio, accIndex) => (
                                                      <span
                                                        key={accIndex}
                                                        className="badge text-dark m-1 custom-tooltip"
                                                        style={{
                                                          backgroundColor:
                                                            "rgba(5, 236, 248, 0.79)",
                                                          color: "white",
                                                          borderRadius: "8px",
                                                          padding: "5px 10px",
                                                          margin: "5px",
                                                          boxShadow:
                                                            "rgba(0, 0, 0, 0.1) 0px 2px 6px",
                                                          cursor: "pointer",
                                                          position: "relative", // necesario para que el tooltip se posicione bien
                                                          display:
                                                            "inline-block",
                                                        }}
                                                      >
                                                        {accesorio.title ||
                                                          accesorio}
                                                        {accesorio.description && (
                                                          <span className="tooltip-centered">
                                                            <svg
                                                              xmlns="http://www.w3.org/2000/svg"
                                                              width="18"
                                                              height="18"
                                                              fill="currentColor"
                                                              viewBox="0 0 16 16"
                                                              style={{
                                                                marginRight:
                                                                  "8px",
                                                                verticalAlign:
                                                                  "middle",
                                                                color:
                                                                  "#5bc0de",
                                                              }}
                                                            >
                                                              <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.93 10.588-2.29.287-.082-.38.45-.083c.294-.07.352-.176.288-.469l-.738-3.468c-.194-.897.105-1.319.808-1.319.545 0 .875.252 1.07.598l.088.416c.063.29.025.352-.288.469l-.451.083.082.381 1.287-.287c.287 0 .451.176.451.469 0 .287-.164.466-.451.466zm-.81-6.537a.905.905 0 1 1 1.81 0 .905.905 0 0 1-1.81 0z" />
                                                            </svg>
                                                            {
                                                              accesorio.description
                                                            }
                                                          </span>
                                                        )}
                                                      </span>
                                                    )
                                                  )}
                                                </div>
                                              ) : (
                                                <p className="text-muted">
                                                  No se han seleccionado
                                                  accesorios.
                                                </p>
                                              )}

                                              {/* MENSUALIDADES DEL PLAN */}
                                              {totalPlan > 0 && (
                                                <div
                                                  className="mt-3"
                                                  style={{ fontSize: "0.8rem" }}
                                                >
                                                  <div>
                                                    36 meses /{" "}
                                                    {new Intl.NumberFormat(
                                                      "es-MX",
                                                      {
                                                        style: "currency",
                                                        currency: "MXN",
                                                        maximumFractionDigits: 0,
                                                      }
                                                    ).format(
                                                      mensualidad36
                                                    )}{" "}
                                                    Mensual
                                                  </div>
                                                  <div>
                                                    48 meses /{" "}
                                                    {new Intl.NumberFormat(
                                                      "es-MX",
                                                      {
                                                        style: "currency",
                                                        currency: "MXN",
                                                        maximumFractionDigits: 0,
                                                      }
                                                    ).format(
                                                      mensualidad48
                                                    )}{" "}
                                                    Mensual
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/*buscador */}
                    <div className="sticky-search row mb-3">
                      <div className="col-12">
                        <div className="search-box position-relative">
                          <input
                            type="text"
                            className="form-control border-0 fs-5 pe-5"
                            placeholder="Buscar vehículo…"
                            value={searchVehicle}
                            onChange={(e) => setsearchVehicle(e.target.value)}
                          />

                          {/*botn ✕ para limpiar */}
                          {searchVehicle && (
                            <button
                              type="button"
                              className="btn search-clear-btn"
                              onClick={() => setsearchVehicle("")}
                              aria-label="Limpiar búsqueda"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="row g-3">
                      {tableData && tableData.food ? (
                        (() => {
                          const normalize = (str = "") =>
                            str
                              .toLocaleLowerCase("es-MX")
                              .normalize("NFD")
                              .replace(/[\u0300-\u036f]/g, "");

                          const query = normalize(searchVehicle);

                          const filtered = tableData.food.filter((food) => {
                            if (
                              query &&
                              !(
                                normalize(food.title).includes(query) ||
                                normalize(food.modelo ?? "").includes(query) ||
                                normalize(food.description ?? "").includes(query)
                              )
                            )
                              return false;

                            if (food.type === "nuevos" || food.type === "seminuevos") {
                              return true; // siempre mostrar vehículos
                            }

                            return selectedBrand
                              ? food.brand === selectedBrand || food.brand === "all"
                              : food.brand === "all";
                          });

                          if (filtered.length === 0) {
                            return (
                              <div className="col-12 text-center py-5">
                                <h5>No se encontraron resultados</h5>
                              </div>
                            );
                          }

                          return filtered.map((food, index) => (
                            <div
                              className={
                                "col-xl-3 col-md-4 col-6" + (food.hide ? " d-none" : "")
                              }
                              key={index}
                            >
                              <a
                                href="#/"
                                className={
                                  "pos-product" + (!food.available ? " not-available" : "")
                                }
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (
                                    food.type === "accesorios" ||
                                    food.type === "garantias"
                                  ) {
                                    addToCartDirectly(food); // Agregar directamente al carrito
                                  }
                                  showPosItemModal(event, food); // Abrir el modal SIEMPRE
                                }}
                              >
                                <div className="img">
                                  <img
                                    src={food.image}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "/assets/img/product/noimage.png";
                                    }}
                                    alt={food.title}
                                    style={{
                                      width: "100%",
                                      height: "180px",
                                      objectFit: "cover",
                                      backgroundColor: "white",
                                    }}
                                  />
                                </div>

                                <div className="info">
                                  <div className="title">
                                    {food.title}{" "}
                                    {(food.type === "nuevos" || food.type === "seminuevos") &&
                                      food.description}
                                  </div>
                                  <div className="desc">
                                    {food.modelo.length > 100
                                      ? `${food.modelo.slice(0, 100)}...`
                                      : food.modelo}
                                  </div>
                                  {food.type !== "garantias" &&
                                    food.type !== "accesorios" && (
                                      <div className="price">
                                        $
                                        {new Intl.NumberFormat("es-MX", {
                                          style: "decimal",
                                          minimumFractionDigits: 0,
                                          maximumFractionDigits: 0,
                                        }).format(food.price)}
                                      </div>
                                    )}
                                </div>

                                {!food.available && (
                                  <div className="not-available-text">
                                    <div>No disponible</div>
                                  </div>
                                )}
                              </a>
                            </div>
                          ));
                        })()
                      ) : (
                        <div className="col-12">Cargando...</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </PerfectScrollbar>
          </div>

          <div className="pos-sidebar" id="pos-sidebar">
            <div className="h-100 d-flex flex-column p-0">
              <div className="pos-sidebar-header mb-2px d-flex align-items-center justify-content-between">
                <div className="back-btn">
                  <button
                    type="button"
                    onClick={dismissMobileSidebar}
                    className="btn"
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                </div>
                <div className="order d-flex align-items-center black-text">
                  Folio:{" "}
                  <span className="fw-semibold text-white mx-2 black-text">
                    {folioVenta}
                  </span>
                  <img
                    src="/assets/img/product/qr.png"
                    alt="QR Code"
                    className="qr-code"
                  />
                </div>
              </div>

              <div className="pos-sidebar-nav fs-10px">
                <ul className="nav nav-tabs nav-fill">
                  <li className="nav-item">
                    <a
                      className="nav-link fw-semibold active black-text"
                      href="#/"
                      data-bs-toggle="tab"
                      data-bs-target="#newOrderTab"
                    >
                      ORDEN ({getOrderTotal()})
                    </a>
                  </li>
                  {/*<li className="nav-item">
                    <a
                      className="nav-link fw-semibold"
                      href="#/"
                      data-bs-toggle="tab"
                      data-bs-target="#orderHistoryTab"
                    >
                      RESUMEN
                    </a>
                  </li>
                  <li className="nav-item">
                    <a
                      className="nav-link fw-semibold"
                      href="#/"
                      data-bs-toggle="tab"
                      data-bs-target="#plansTab"
                    >
                      PLANES
                    </a>
                  </li>*/}
                </ul>
              </div>

              <PerfectScrollbar className="pos-sidebar-body tab-content h-100">
                <div
                  className="tab-pane fade h-100 show active"
                  id="newOrderTab"
                >
                  {orderData && orderData.length > 0 ? (
                    orderData
                      .filter(
                        (order) =>
                          order.type !== "accesorios" &&
                          order.type !== "garantias"
                      )
                      .map((order, index) => (
                        <div className="pos-order" key={index}>
                          <div className="pos-order-product">
                            <div
                              className="img"
                              style={{
                                backgroundImage: "url(" + order.image + ")",
                              }}
                            ></div>
                            <div className="flex-1">
                              <div className="fw-semibold text-white black-text">
                                {order.title} {order.description}
                              </div>
                              <div className="mb-1 black-text">
                                $
                                {new Intl.NumberFormat("es-MX", {
                                  style: "decimal",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(selectedMailData?.precio_vehiculo)}
                              </div>
                            </div>
                          </div>
                          <div className="pos-order-price d-flex flex-column">
                            <div className="flex-1 h6 mb-0 black-text">
                              $
                              {new Intl.NumberFormat("es-MX", {
                                style: "decimal",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(selectedMailData?.precio_vehiculo * order.quantity)}
                            </div>
                          </div>

                          {order.confirmation && (
                            <div className="pos-order-confirmation text-center d-flex flex-column justify-content-center">
                              <div className="mb-1">
                                <i className="bi bi-trash fs-36px lh-1"></i>
                              </div>
                              <div className="mb-2">¿Remover?</div>
                              <div>
                                <button
                                  onClick={(event) =>
                                    toggleConfirmation(event, order.id, false)
                                  }
                                  className="btn btn-outline-white btn-sm ms-auto me-2 width-100px"
                                >
                                  No
                                </button>
                                <button
                                  onClick={(event) =>
                                    removeOrder(event, order.id)
                                  }
                                  className="btn btn-outline-theme btn-sm width-100px"
                                >
                                  Sí
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                  ) : (
                    <div className="h-100 d-flex align-items-center justify-content-center text-center">
                      <div>
                        <div className="mb-3 mt-n5">
                          <svg
                            width="6em"
                            height="6em"
                            viewBox="0 0 16 16"
                            className="text-gray-300"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              d="M14 5H2v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5zM1 4v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4H1z"
                            />
                            <path d="M8 1.5A2.5 2.5 0 0 0 5.5 4h-1a3.5 3.5 0 1 1 7 0h-1A2.5 2.5 0 0 0 8 1.5z" />
                          </svg>
                        </div>
                        <h5>No se encontró ningún vehículo o accesorio</h5>
                      </div>
                    </div>
                  )}
                </div>

                {/*<div className="tab-pane fade h-100" id="orderHistoryTab">
                  <div className="h-100 d-flex align-items-center justify-content-center text-center">
                    <div className="row w-100 p-4">
                      <div className="col-12 text-center mb-4">
                        <img
                          src={
                            vehicleData?.url_img ||
                            "https://via.placeholder.com/300x200"
                          }
                          alt="Vehículo"
                          className="img-fluid rounded shadow"
                          style={{ maxWidth: "350px", height: "auto" }}
                        />
                      </div>

                      {/* Grupo de campos principales 
                      {[
                        {
                          label: "Marca",
                          value: vehicleData?.marca_vehiculo,
                          icon: "fa-car",
                        },
                        {
                          label: "Modelo",
                          value: vehicleData?.modelo_vehiculo,
                          icon: "fa-cube",
                        },
                        {
                          label: "Año",
                          value: vehicleData?.ano_vehiculo,
                          icon: "fa-calendar",
                        },
                        {
                          label: "Agencia",
                          value: vehicleData?.agencia,
                          icon: "fa-building",
                        },
                        {
                          label: "Meses",
                          value:
                            vehicleData?.meses_financiamiento > 0
                              ? `${vehicleData.meses_financiamiento} meses`
                              : "",
                          icon: "fa-calendar-alt",
                        },
                        {
                          label: "Mensualidad*",
                          value: vehicleData?.precio_total
                            ? `$${new Intl.NumberFormat("es-MX", {
                              style: "decimal",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(
                              ((vehicleData.precio_total || 0) *
                                (1 -
                                  (vehicleData.porcentaje_enganche || 0) /
                                  100)) /
                              (vehicleData.meses_financiamiento || 12)
                            )}`
                            : "",
                          icon: "fa-dollar-sign",
                        },
                        {
                          label: "Precio Total*",
                          value: vehicleData?.precio_vehiculo
                            ? formatCurrency(vehicleData.precio_vehiculo)
                            : "",
                          icon: "fa-money-bill-wave",
                        },
                      ].map((item, i) => (
                        <div className="col-md-6 mb-3" key={i}>
                          <label className="fw-bold">
                            <i
                              className={`fas ${item.icon} me-2 text-primary`}
                            ></i>
                            {item.label}
                          </label>
                          <input
                            type="text"
                            className="form-control shadow-sm"
                            value={item.value || ""}
                            readOnly
                          />
                        </div>
                      ))}

                      {/* Enganche con barra 
                      <div className="col-md-6 mb-3">
                        <label className="fw-bold">
                          <i className="fas fa-percentage me-2 text-success"></i>
                          Porcentaje de Enganche:
                        </label>
                        <input
                          type="range"
                          className="form-range"
                          min="0"
                          max="100"
                          value={vehicleData?.porcentaje_enganche || 0}
                          readOnly
                        />
                        <span className="badge bg-success">
                          {vehicleData?.porcentaje_enganche || 0}%
                        </span>
                      </div>*/}

                {/* Accesorios 
                <div className="col-md-12 mb-4">
                  <div className="card shadow-sm border-0">
                    <div className="card-body text-center">
                      <h5 className="card-title text-primary">
                        <i className="fas fa-tools me-2"></i>
                        Accesorios Disponibles
                      </h5>
                      {vehicleData?.productos?.length > 0 ? (
                        vehicleData.productos.map((accesorio, index) => (
                          <span
                            key={index}
                            className="badge bg-primary text-white me-2 mb-2"
                          >
                            {accesorio.title || "Accesorio no disponible"}
                          </span>
                        ))
                      ) : (
                        <p className="text-muted">
                          No se han seleccionado accesorios.
                        </p>
                      )}
                    </div>
                  </div>
                </div>*/}

                {/* Planes 
                      <div className="col-md-12 mb-4">
                        <div className="card shadow-sm border-0">
                          <div className="card-body text-center">
                            <h5 className="card-title text-info">
                              <i className="fas fa-file-contract me-2"></i>
                              Plan
                            </h5>
                            <h6 className="text-muted mb-3">
                              Accesorios incluidos:
                            </h6>
                            {vehicleData?.planesSeleccionados?.length > 0 ? (
                              vehicleData.planesSeleccionados.map(
                                (plan, index) => (
                                  <span
                                    key={index}
                                    className="badge bg-info text-dark me-2 mb-2"
                                  >
                                    {plan.title || "Accesorio no disponible"}
                                  </span>
                                )
                              )
                            ) : (
                              <p className="text-muted">
                                No se han seleccionado accesorios.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>*/}

                <div className="tab-pane fade h-100" id="plansTab">
                  <div className="h-100 align-items-center justify-content-center text-center">
                    <div className="row">
                      {/* Iteramos sobre los 3 planes de financiamiento */}
                      {vehicleData?.planesFinanciamiento?.map((plan, index) => (
                        <div className="col-md-12 mb-4" key={index}>
                          <div className="card">
                            <div className="card-body text-center">
                              <h5 className="card-title">{plan.title}</h5>

                              {/* Mostrar los accesorios de cada plan */}
                              <h6 className="card-price">
                                Accesorios incluidos:
                              </h6>
                              {plan.accessories &&
                                plan.accessories.length > 0 ? (
                                plan.accessories.map((accesorio, accIndex) => (
                                  <span
                                    key={accIndex}
                                    className="badge bg-info text-dark me-2"
                                  >
                                    {accesorio.title}
                                  </span>
                                ))
                              ) : (
                                <p>No se han seleccionado accesorios.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </PerfectScrollbar>

              <div className="pos-sidebar-footer">
                {selectedMailData?.depende_auto === "Si" ? ( //contado si
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <div className="fw-bold fs-5 text-muted">Precio Total</div>
                      <div className="flex-1 text-end h4 mb-0 text-primary fw-semibold">
                        {vehicleData?.precio_vehiculo
                          ? `$${new Intl.NumberFormat("es-MX", {
                            style: "decimal",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(vehicleData.precio_vehiculo)}`
                          : "N/A"}
                      </div>
                    </div>

                    <hr className="my-3 opacity-50" />

                    <div className="d-flex align-items-center mb-3">
                      <div className="fw-bold fs-5 text-muted">Mensualidad de adicionales</div>
                      <div className="flex-1 text-end h4 mb-0 text-success fw-semibold">
                        {vehicleData?.mensualidad_estimacion
                          ? `$${new Intl.NumberFormat("es-MX", {
                            style: "decimal",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(vehicleData.mensualidad_estimacion)}`
                          : vehicleData?.precio_total
                            ? `$${new Intl.NumberFormat("es-MX", {
                              style: "decimal",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(
                              ((vehicleData.precio_total || 0) *
                                (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                              (vehicleData.meses_financiamiento || 12)
                            )}`
                            : ""}
                      </div>
                    </div>
                  </>

                ) : (
                  // Mostrar mensualidades para crédito
                  <>
                    <div className="d-flex align-items-center mb-3">
                      <div className="fw-bold fs-5 text-muted">Mensualidad sin adicionales</div>
                      <div className="flex-1 text-end h4 mb-0 text-primary fw-semibold">
                        {vehicleData?.precio_vehiculo && vehicleData?.mensualidad_estimacion
                          ? `$${new Intl.NumberFormat("es-MX", {
                            style: "decimal",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(
                            Math.round(
                              (vehicleData.mensualidad_estimacion * vehicleData.precio_vehiculo) /
                              vehicleData.precio_total
                            )
                          )}`
                          : vehicleData?.precio_vehiculo
                            ? `$${new Intl.NumberFormat("es-MX", {
                              style: "decimal",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(
                              ((vehicleData.precio_vehiculo || 0) *
                                (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                              (vehicleData.meses_financiamiento || 12)
                            )}`
                            : ""}
                      </div>
                    </div>

                    <hr className="my-3 opacity-50" />

                    <div className="d-flex align-items-center mb-3">
                      <div className="fw-bold fs-5 text-muted">Mensualidad con adicionales</div>
                      <div className="flex-1 text-end h4 mb-0 text-success fw-semibold">
                        {vehicleData?.mensualidad_estimacion
                          ? `$${new Intl.NumberFormat("es-MX", {
                            style: "decimal",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(vehicleData.mensualidad_estimacion)}`
                          : vehicleData?.precio_total
                            ? `$${new Intl.NumberFormat("es-MX", {
                              style: "decimal",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            }).format(
                              ((vehicleData.precio_total || 0) *
                                (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                              (vehicleData.meses_financiamiento || 12)
                            )}`
                            : ""}
                      </div>
                    </div>
                  </>
                )}

                {/* Leyenda */}
                <div className="text-muted small fst-italic text-center">
                  *El precio total mostrado no incluye accesorios ni garantías
                  adicionales.
                </div>

                <div className="mt-3">
                  <div className="btn-group d-flex">
                    {/* <button
                      onClick={handleShowModalEstadisticas}
                      className="btn btn-white btn-sm flex-fill d-flex flex-column align-items-center justify-content-center"
                      disabled={isDisabled} 
                    >
                      <Icon
                        icon="material-symbols-light:send"
                        className="fs-24px d-flex justify-content-center"
                      />
                      <span className="fw-semibold">COTIZAR</span>
                    </button>*/}
                    <button
                      className="btn btn-outline-primary mb-2"
                      onClick={() => setCanvasActivo((prev) => !prev)}
                    >
                      {canvasActivo ? "Desactivar pincel" : "Activar pincel"}
                    </button>
                    <button
                      className="btn btn-outline-primary mb-2"
                      onClick={() => canvasRef.current?.clearCanvas()}
                    >
                      Borrar todo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div >
      </div >

      <a
        href="#/"
        className="pos-mobile-sidebar-toggler carrito-toggle"
        data-bs-toggle="modal"
        data-bs-target="#orderModal"
      >
        <i className="bi bi-bag"></i>
        <span className="badge">{getOrderTotal()}</span>
      </a>
      <a
        href="#/"
        className="pos-mobile-sidebar-toggler pincel-toggle"
        onClick={() => setCanvasActivo((prev) => !prev)}
      >
        <i className={`bi ${canvasActivo ? "bi-brush-fill" : "bi-brush"}`}></i>
        <span className="badge">{canvasActivo ? "ON" : "OFF"}</span>
      </a>

      <a
        href="#/"
        className="pos-mobile-sidebar-toggler borrar-toggle"
        onClick={() => canvasRef.current?.clearCanvas()}
      >
        <i className="bi bi-trash"></i>
      </a>

      <div
        className="modal fade"
        id="orderModal"
        tabIndex="-1"
        aria-labelledby="orderModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="orderModalLabel">
                Detalles de la Orden
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body p-0">
              <div className="pos-sidebar">
                <div className="h-100 d-flex flex-column">
                  <div className="pos-sidebar-header mb-2px d-flex align-items-center justify-content-between">
                    <div className="order d-flex align-items-center">
                      Folio:{" "}
                      <span className="fw-semibold text-white mx-2">
                        {folioVenta}
                      </span>
                      <img
                        src="/assets/img/product/qr.png"
                        alt="QR Code"
                        className="qr-code"
                      />
                    </div>
                  </div>

                  <div className="pos-sidebar-nav fs-10px">
                    <ul className="nav nav-tabs nav-fill">
                      <li className="nav-item">
                        <a
                          className="nav-link fw-semibold active"
                          href="#/"
                          data-bs-toggle="tab"
                          data-bs-target="#newOrderTabMobile"
                        >
                          Accesorios ({getOrderTotal()})
                        </a>
                      </li>
                      {/*<li className="nav-item">
                        <a
                          className="nav-link fw-semibold"
                          href="#/"
                          data-bs-toggle="tab"
                          data-bs-target="#orderHistoryTabMobile"
                        >
                          Detalles del Vehículo
                        </a>
                      </li>
                      <li className="nav-item">
                        <a
                          className="nav-link fw-semibold"
                          href="#/"
                          data-bs-toggle="tab"
                          data-bs-target="#plansTabMobile"
                        >
                          Planes
                        </a>
                      </li>*/}
                    </ul>
                  </div>

                  <PerfectScrollbar className="pos-sidebar-body tab-content h-100">
                    {orderData && orderData.length > 0 ? (
                      <div className="row">
                        {orderData
                          .filter(
                            (order) =>
                              order.type !== "accesorios" &&
                              order.type !== "garantias"
                          )
                          .map((order, index) => (
                            <div className="col-12 mb-3" key={index}>
                              <div className="card">
                                <div className="card-body">
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={order.image}
                                      alt={order.title}
                                      className="rounded me-3"
                                      style={{
                                        width: "100px",
                                        height: "auto",
                                      }}
                                    />
                                    <div className="flex-grow-1">
                                      <h5 className="mb-2">
                                        {order.title} {order.description}
                                      </h5>
                                      <div className="d-flex align-items-center">
                                        <span className="me-3">
                                          <div className="mb-1">
                                            $
                                            {new Intl.NumberFormat("es-MX", {
                                              style: "decimal",
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 0,
                                            }).format(selectedMailData?.precio_vehiculo)}
                                          </div>
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="row h-100">
                        <div className="col-12 d-flex flex-column align-items-center justify-content-center py-5 pt-4 pb-4">
                          <svg
                            width="6em"
                            height="6em"
                            viewBox="0 0 16 16"
                            className="text-gray-300 mb-3"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              d="M14 5H2v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5zM1 4v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4H1z"
                            />
                            <path d="M8 1.5A2.5 2.5 0 0 0 5.5 4h-1a3.5 3.5 0 1 1 7 0h-1A2.5 2.5 0 0 0 8 1.5z" />
                          </svg>
                          <h5>No se encontró ningún vehículo o accesorio</h5>
                        </div>
                      </div>
                    )}

                    <div
                      className="tab-pane fade h-100"
                      id="orderHistoryTabMobile"
                    >
                      {/*<div className="h-100 align-items-center justify-content-center text-center">
                        <div className="row">
                          {/* <div className="col-12 text-center mb-3">
                            <img
                              src={
                                vehicleData?.url_img ||
                                "https://via.placeholder.com/300x200"
                              }
                              alt="Vehículo"
                              className="img-fluid rounded"
                              style={{ maxWidth: "300px", height: "auto" }}
                            />
                          </div> 

                          {[
                            {
                              label: "Marca del Vehículo",
                              value: vehicleData?.marca_vehiculo || "",
                              icon: "fa-car",
                            },
                            {
                              label: "Modelo del Vehículo",
                              value: vehicleData?.modelo_vehiculo || "",
                              icon: "fa-car-side",
                            },
                            {
                              label: "Año del Vehículo",
                              value: vehicleData?.ano_vehiculo || "",
                              icon: "fa-calendar",
                            },
                            {
                              label: "Agencia",
                              value: vehicleData?.agencia || "",
                              icon: "fa-building",
                            },
                            {
                              label: "Meses",
                              value:
                                vehicleData?.meses_financiamiento > 0
                                  ? `${vehicleData.meses_financiamiento} meses`
                                  : "",
                              icon: "fa-calendar-alt",
                            },
                            {
                              label: "Mensualidad*",
                              value: vehicleData?.precio_total
                                ? `$${new Intl.NumberFormat("es-MX", {
                                  style: "decimal",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(
                                  ((vehicleData.precio_total || 0) *
                                    (1 -
                                      (vehicleData.porcentaje_enganche || 0) /
                                      100)) /
                                  (vehicleData.meses_financiamiento || 12)
                                )}`
                                : "",
                              icon: "fa-dollar-sign",
                            },
                            {
                              label: "Precio Total*",
                              value: formatCurrency(
                                vehicleData?.precio_vehiculo
                              ),
                              icon: "fa-money-bill-wave",
                            },
                          ].map((item, index) => (
                            <div
                              className="col-md-6 mb-3 text-start"
                              key={index}
                            >
                              <label className="fw-bold text-muted">
                                <i
                                  className={`fas ${item.icon} me-2 text-primary`}
                                ></i>
                                {item.label}
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={item.value}
                                readOnly
                              />
                            </div>
                          ))}

                          {/* Enganche con barra 
                          <div className="col-md-6 mb-3">
                            <label className="fw-bold">
                              <i className="fas fa-percentage me-2 text-success"></i>
                              Porcentaje de Enganche:
                            </label>
                            <input
                              type="range"
                              className="form-range"
                              min="0"
                              max="100"
                              value={vehicleData?.porcentaje_enganche || 0}
                              readOnly
                            />
                            <span className="badge bg-success">
                              {vehicleData?.porcentaje_enganche || 0}%
                            </span>
                          </div>

                          {/* Accesorios 
                          <div className="col-md-12 mb-4">
                            <div className="card">
                              <div className="card-body text-center">
                                <h5 className="card-title">
                                  <i className="fas fa-tools me-2 text-secondary"></i>
                                  Accesorios disponibles
                                </h5>
                                {vehicleData?.productos?.length > 0 ? (
                                  vehicleData.productos.map(
                                    (accesorio, index) => (
                                      <span
                                        key={index}
                                        className="badge bg-primary text-white me-2 mb-1"
                                      >
                                        {accesorio.title ||
                                          "Accesorio no disponible"}
                                      </span>
                                    )
                                  )
                                ) : (
                                  <p>No se han seleccionado accesorios.</p>
                                )}
                              </div>
                            </div>
                          </div>

                          
                          <div className="col-md-12 mb-4">
                            <div className="card">
                              <div className="card-body text-center">
                                <h5 className="card-title">
                                  <i className="fas fa-file-contract me-2 text-info"></i>
                                  Plan
                                </h5>
                                {vehicleData?.planesSeleccionados?.length >
                                  0 ? (
                                  vehicleData.planesSeleccionados.map(
                                    (plan, index) => (
                                      <span
                                        key={index}
                                        className="badge bg-info text-dark me-2 mb-1"
                                      >
                                        {plan.title ||
                                          "Accesorio no disponible"}
                                      </span>
                                    )
                                  )
                                ) : (
                                  <p>No se han seleccionado accesorios.</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>*/}
                    </div>

                    {/*<div className="tab-pane fade h-100" id="plansTabMobile">
                      <div className="h-100 align-items-center justify-content-center text-center">
                        <div className="row">
                          {/* Iteramos sobre los 3 planes de financiamiento 
                          {vehicleData?.planesFinanciamiento?.map(
                            (plan, index) => (
                              <div className="col-md-12 mb-4" key={index}>
                                <div className="card">
                                  <div className="card-body text-center">
                                    <h5 className="card-title">{plan.title}</h5>

                                    {/* Mostrar los accesorios de cada plan 
                                    <h6 className="card-price">
                                      Accesorios incluidos:
                                    </h6>
                                    {plan.accessories &&
                                      plan.accessories.length > 0 ? (
                                      plan.accessories.map(
                                        (accesorio, accIndex) => (
                                          <span
                                            key={accIndex}
                                            className="badge bg-info text-dark me-2"
                                          >
                                            {accesorio}
                                          </span>
                                        )
                                      )
                                    ) : (
                                      <p>No se han seleccionado accesorios.</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>*/}
                  </PerfectScrollbar>

                  <div className="pos-sidebar-footer">
                    {selectedMailData?.depende_auto === "Si" ? ( //contado si
                      <>
                        <div className="d-flex align-items-center mb-3">
                          <div className="fw-bold fs-5 text-muted">Precio Total</div>
                          <div className="flex-1 text-end h4 mb-0 text-primary fw-semibold">
                            {vehicleData?.precio_vehiculo
                              ? `$${new Intl.NumberFormat("es-MX", {
                                style: "decimal",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(vehicleData.precio_vehiculo)}`
                              : "N/A"}
                          </div>
                        </div>

                        <hr className="my-3 opacity-50" />

                        <div className="d-flex align-items-center mb-3">
                          <div className="fw-bold fs-5 text-muted">Mensualidad de adicionales</div>
                          <div className="flex-1 text-end h4 mb-0 text-success fw-semibold">
                            {vehicleData?.mensualidad_estimacion
                              ? `$${new Intl.NumberFormat("es-MX", {
                                style: "decimal",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(vehicleData.mensualidad_estimacion)}`
                              : vehicleData?.precio_total
                                ? `$${new Intl.NumberFormat("es-MX", {
                                  style: "decimal",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(
                                  ((vehicleData.precio_total || 0) *
                                    (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                                  (vehicleData.meses_financiamiento || 12)
                                )}`
                                : ""}
                          </div>
                        </div>
                      </>

                    ) : (
                      // Mostrar mensualidades para crédito
                      <>
                        <div className="d-flex align-items-center mb-3">
                          <div className="fw-bold fs-5 text-muted">Mensualidad sin adicionales</div>
                          <div className="flex-1 text-end h4 mb-0 text-primary fw-semibold">
                            {vehicleData?.precio_vehiculo && vehicleData?.mensualidad_estimacion
                              ? `$${new Intl.NumberFormat("es-MX", {
                                style: "decimal",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(
                                Math.round(
                                  (vehicleData.mensualidad_estimacion * vehicleData.precio_vehiculo) /
                                  vehicleData.precio_total
                                )
                              )}`
                              : vehicleData?.precio_vehiculo
                                ? `$${new Intl.NumberFormat("es-MX", {
                                  style: "decimal",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(
                                  ((vehicleData.precio_vehiculo || 0) *
                                    (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                                  (vehicleData.meses_financiamiento || 12)
                                )}`
                                : ""}
                          </div>
                        </div>

                        <hr className="my-3 opacity-50" />

                        <div className="d-flex align-items-center mb-3">
                          <div className="fw-bold fs-5 text-muted">Mensualidad con adicionales</div>
                          <div className="flex-1 text-end h4 mb-0 text-success fw-semibold">
                            {vehicleData?.mensualidad_estimacion
                              ? `$${new Intl.NumberFormat("es-MX", {
                                style: "decimal",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(vehicleData.mensualidad_estimacion)}`
                              : vehicleData?.precio_total
                                ? `$${new Intl.NumberFormat("es-MX", {
                                  style: "decimal",
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }).format(
                                  ((vehicleData.precio_total || 0) *
                                    (1 - (vehicleData.porcentaje_enganche || 0) / 100)) /
                                  (vehicleData.meses_financiamiento || 12)
                                )}`
                                : ""}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Leyenda */}
                    <div className="text-muted small fst-italic text-center">
                      *El precio total mostrado no incluye accesorios ni garantías
                      adicionales.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-primary mb-2"
                data-bs-dismiss="modal"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Modal */}
      <div className="modal modal-pos fade" id="modalPosItem">
        <div className="modal-dialog modal-lg">
          <div className="modal-content border-0">
            {modalData && (
              <>
                <div className="modal-pos-product">
                  <div className="modal-pos-product-img d-flex flex-column">
                    {/* Mostrar la imagen solo si no hay video */}
                    {!modalData.video && (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "250px", backgroundColor: "white" }}
                      >
                        <img
                          src={modalData.image}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/assets/img/product/noimage.png";
                          }}
                          alt="Producto"
                          style={{
                            maxHeight: "100%",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    )}

                    {/* Mostrar video solo si existe */}
                    {modalData.video && (
                      <div className="modal-pos-product-video mt-3">
                        <video width="100%" controls>
                          <source src={modalData.video} type="video/mp4" />
                          Tu navegador no soporta el tag de video.
                        </video>
                      </div>
                    )}
                  </div>

                  <div className="modal-pos-product-info">
                    <div className="fs-4 fw-semibold black-text">
                      {modalData.title}{" "}
                      {(modalData.type === "nuevos" ||
                        modalData.type === "seminuevos") &&
                        modalData.description}
                    </div>
                    <div className="fs-4 fw-semibold black-text">
                      {modalData.year}
                    </div>
                    <div className="fs-4 fw-semibold d-none">
                      {modalData.type}
                    </div>
                    <div className="text-body text-opacity-50 mb-2">
                      {modalData.modelo.length > 200
                        ? `${modalData.modelo.slice(0, 200)}...`
                        : modalData.modelo}
                    </div>

                    {modalData.type !== "accesorios" &&
                      modalData.type !== "garantias" && (
                        <div className="fs-3 fw-bold mb-3 black-text">
                          Desde: $
                          {new Intl.NumberFormat("es-MX", {
                            style: "decimal",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }).format(modalData.price)}
                        </div>
                      )}

                    <hr className="opacity-1" />
                    <div className="row">
                      <div className="col-4">
                        <button
                          className="btn btn-default fw-semibold mb-0 d-block py-3 w-100"
                          data-bs-dismiss="modal"
                        >
                          Cerrar
                        </button>
                      </div>
                      {modalData.type !== "accesorios" &&
                        modalData.type !== "garantias" && (
                          <div className="col-8">
                            <button
                              className="btn btn-theme fw-semibold d-flex justify-content-center align-items-center py-3 m-0 w-100"
                              onClick={(event) => {
                                addToCart(event);
                                setSelectedCar(modalData); // guardar el auto seleccionado
                                // Opcional: cerrar el modal
                                document
                                  .getElementById("modalPosItem")
                                  ?.classList.remove("show");
                                document.body.classList.remove("modal-open");
                                document
                                  .querySelector(".modal-backdrop")
                                  ?.remove();
                              }}
                              disabled={false}
                            >
                              Me interesa{" "}
                              <i className="bi bi-plus fa-2x ms-2 my-n3"></i>
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="accessoryModal"
        tabIndex="-1"
        aria-labelledby="accessoryModalLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="accessoryModalLabel">
                Comparación de Accesorios
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <DragDropContext
              //onDragEnd={handleDragEndModal}
              onDragStart={() => {
                document.body.style.cursor = "grabbing";
              }}
              onDragUpdate={() => {
                document.body.style.cursor = "grabbing";
              }}
            >
              <div className="modal-body">
                <div className="row mt-4">
                  <div className="col-12">
                    <h5>
                      Mensualidad Estimada:
                      {vehicleData?.precio_total
                        ? ` $${new Intl.NumberFormat("es-MX", {
                          style: "decimal",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(
                          ((vehicleData.precio_total || 0) *
                            (1 -
                              (vehicleData.porcentaje_enganche || 0) / 100)) /
                          (vehicleData.meses_financiamiento || 12)
                        )}`
                        : ""}
                    </h5>
                  </div>
                  <div className="col-12">
                    <h6 className="mb-3">Garantía Limitada</h6>
                    {/* --- Motor --- */}
                    <div className="row align-items-center mb-3 gy-2">
                      <div className="col-md-7 d-flex">
                        <span className="warranty-icon me-3">
                          <i className="fa-solid fa-cogs"></i>
                        </span>
                        <div>
                          <h5 className="fw-bold mb-1">Motor</h5>
                          <p className="mb-0 small">
                            Cubre defectos de fabricación en componentes mecánicos y eléctricos.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="warranty-box text-whitee text-center fw-bold">
                          3 AÑOS / 100,000 KM
                          <div className="border-top pt-1">2 AÑOS / 40,000 KM</div>
                        </div>
                      </div>
                    </div>

                    {/* --- Carrocería --- */}
                    <div className="row align-items-center mb-3 gy-2">
                      <div className="col-md-7 d-flex">
                        <span className="warranty-icon me-3">
                          <i className="fa-solid fa-shield-halved"></i>
                        </span>
                        <div>
                          <h5 className="fw-bold mb-1">Carrocería</h5>
                          <p className="mb-0 small">
                            Cubre defectos de corrosión perforante desde el interior hacia el exterior.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="warranty-box text-whitee text-center fw-bold">
                          6 AÑOS
                          <div className="border-top pt-1">2 AÑOS</div>
                        </div>
                      </div>
                    </div>

                    {/* --- Pintura --- */}
                    <div className="row align-items-center mb-3 gy-2">
                      <div className="col-md-7 d-flex">
                        <span className="warranty-icon me-3">
                          <i className="fa-solid fa-fill-drip"></i>
                        </span>
                        <div>
                          <h5 className="fw-bold mb-1">Pintura</h5>
                          <p className="mb-0 small">
                            Cubre defectos de pintura relacionados con el proceso de fabricación.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="warranty-box text-whitee text-center fw-bold">
                          3 AÑOS
                          <div className="border-top pt-1">SIN LÍMITE DE KM</div>
                        </div>
                      </div>
                    </div>

                    {/* --- Sistema anticorrosión --- */}
                    <div className="row align-items-center mb-3 gy-2">
                      <div className="col-md-7 d-flex">
                        <span className="warranty-icon me-3">
                          <i className="fa-solid fa-biohazard"></i>
                        </span>
                        <div>
                          <h5 className="fw-bold mb-1">Sistema Anticorrosión</h5>
                          <p className="mb-0 small">
                            Protege contra la corrosión estructural del vehículo.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="warranty-box text-whitee text-center fw-bold">
                          6 AÑOS
                          <div className="border-top pt-1">SIN LÍMITE DE KM</div>
                        </div>
                      </div>
                    </div>

                    {/* --- Sistema de escape --- */}
                    <div className="row align-items-center mb-3 gy-2">
                      <div className="col-md-7 d-flex">
                        <span className="warranty-icon me-3">
                          <i className="fa-solid fa-cloud-bolt"></i>
                        </span>
                        <div>
                          <h5 className="fw-bold mb-1">Sistema de Escape</h5>
                          <p className="mb-0 small">
                            Cubre defectos de fabricación en el sistema de escape.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="warranty-box text-whitee text-center fw-bold">
                          2 AÑOS
                          <div className="border-top pt-1">40,000 KM</div>
                        </div>
                      </div>
                    </div>

                    {/* --- Batería --- */}
                    <div className="row align-items-center mb-3 gy-2">
                      <div className="col-md-7 d-flex">
                        <span className="warranty-icon me-3">
                          <i className="fa-solid fa-car-battery"></i>
                        </span>
                        <div>
                          <h5 className="fw-bold mb-1">Batería</h5>
                          <p className="mb-0 small">
                            Cubre defectos de fabricación en la batería original del vehículo.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="warranty-box text-whitee text-center fw-bold">
                          2 AÑOS
                          <div className="border-top pt-1">50,000 KM</div>
                        </div>
                      </div>
                    </div>

                    {/* --- Audio y multimedia --- */}
                    <div className="row align-items-center mb-3 gy-2">
                      <div className="col-md-7 d-flex">
                        <span className="warranty-icon me-3">
                          <i className="fa-solid fa-music"></i>
                        </span>
                        <div>
                          <h5 className="fw-bold mb-1">Sistema de Audio y Multimedia</h5>
                          <p className="mb-0 small">
                            Cubre defectos en el sistema de audio y multimedia instalado de fábrica.
                          </p>
                        </div>
                      </div>
                      <div className="col-md-5">
                        <div className="warranty-box text-whitee text-center fw-bold">
                          3 AÑOS
                          <div className="border-top pt-1">100,000 KM</div>
                        </div>
                      </div>
                    </div>

                    {/* --- Nota legal --- */}
                    <p className="small fw-bold text-center warranty-disclaimer mt-4">
                      LA GARANTÍA DE MARCA NO APLICA PARA VEHÍCULOS DE USO ESPECIAL,
                      VEHÍCULOS MODIFICADOS, AUTOS DE RENTA Y USO COMERCIAL
                    </p>
                  </div>

                  {/*<div className="row mt-4">
                    <div className="col-12">
                      <h6>¿Qué podrías perder sin estos accesorios?</h6>
                      <ul className="list-group">

                      </ul>
                    </div>
                  </div>

                  <div className="row mt-4">
                    <div className="col-md-6">
                      <h6>Siniestralidad de Llantas por Año</h6>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={llantasData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                          <YAxis unit="%" stroke="var(--axis-stroke)" />
                          <Tooltip formatter={(value) => `${value}%`} />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="siniestralidad"
                            stroke="#dc3545"
                            name="Siniestralidad"
                            label={renderLineLabel}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="col-md-6">
                      <h6>Costo Promedio por Siniestro (Llantas)</h6>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={llantasData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                          <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar
                            dataKey="costo"
                            fill="#28a745"
                            name="Costo Promedio"
                            label={renderBarLabel}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="col-md-6 mt-4">
                      <h6>Siniestralidad Cobertura Mecánica</h6>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={mecanicaData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                          <YAxis unit="%" stroke="var(--axis-stroke)" />
                          <Tooltip formatter={(value) => `${value}%`} />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="siniestralidad"
                            stroke="#007bff"
                            name="Siniestralidad"
                            label={renderLineLabel}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="col-md-6 mt-4">
                      <h6>Costo Promedio por Siniestro (Mecánica)</h6>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={mecanicaData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                          <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar
                            dataKey="costo"
                            fill="#ffc107"
                            name="Costo Promedio"
                            label={renderBarLabel}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="col-md-6 mt-4">
                      <h6>Evolución del costo de reparaciones</h6>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={reparacionesData} margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                          <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                          <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />

                          <Bar dataKey="costo" name="Costo de Reparaciones" label={renderBarLabel}>
                            {reparacionesData.map((entry, index) => (
                              <Cell key={`bar-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="col-md-6 mt-4">
                      <h6>Evolución de Siniestralidad</h6>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Tooltip formatter={(value) => `${value}%`} />
                          <Legend />
                          <Pie
                            data={siniestralidadData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            label={({ value }) => `${value}%`}
                          >
                            {siniestralidadData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>*/ }
                </div>
                <div className="row mt-4">
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label>¿Rechazar garantía extendida?</label>
                      <div>
                        <div className="form-check form-check-inline">
                          <input
                            type="radio"
                            className="form-check-input"
                            name="garantia"
                            id="garantiaSi"
                            value="Si"
                            checked={garantia_extendida === "Si"}
                            onChange={() => {
                              setMostrarModalInfo(true); // Abrir modal informativo
                              setGarantiaExtendida(""); // Reseteamos mientras no confirme
                            }} // Configura el estado a "Si"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="garantiaSi"
                          >
                            Sí
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            type="radio"
                            className="form-check-input"
                            name="garantia"
                            id="garantiaNo"
                            value="No"
                            checked={garantia_extendida === "No"}
                            onChange={() => setGarantiaExtendida("No")} // Configura el estado a "No"
                          />
                          <label
                            className="form-check-label"
                            htmlFor="garantiaNo"
                          >
                            No
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DragDropContext>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={generarDocumentos} // Descargar los pdfs
                disabled={isSubmitting || !rechazoSeleccionado}
              >
                {isSubmitting ? "Enviando..." : "Confirmar y Enviar"}
              </button>
            </div>
          </div>
          {mostrarModalInfo && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              role="dialog"
              style={{
                backgroundColor: "rgba(0,0,0,0.1)",
                backdropFilter: "blur(4px)",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div
                className="modal-dialog modal-xl"
                role="document"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div
                  className="modal-content"
                  style={{ backgroundColor: "rgba(34, 56, 64)" }}
                >
                  <div className="modal-header" style={{ color: "white" }}>
                    <h5 className="modal-title">
                      Antes de rechazar la garantía...
                    </h5>
                    {/*<button
                      type="button"
                      className="btn-close"
                      onClick={() => setMostrarModalInfo(false)}
                    ></button>*/}
                  </div>
                  <div className="modal-body" style={{ color: "white" }}>
                    <div
                      className="p-3 rounded mt-4 modal-info"
                      style={{ backgroundColor: "#1f2e35" }}
                    >
                      <h5 className="text-warning text-center mb-3">
                        ¡Garantiza hoy, maneja tranquilo mañana!
                      </h5>
                      <br></br>
                      <div className="row">
                        <div className="col-md-6">
                          <ul className="text-light text-base list-unstyled">
                            <li className="card-hover d-flex align-items-start">
                              <i className="fa-solid fa-circle-dot text-success me-2 mt-1 icon-sm"></i>
                              <span>
                                <strong>Rines:</strong> La reparación puede costarte al menos $3,500.00,
                                pero reemplazar un rin nuevo de 16” puede ir de $4,800 hasta $5,000*. ¿Vas
                                a correr ese riesgo? ¡Protégete ahora con los planes 5000 y 12000!
                                <sup>¹</sup>
                              </span>
                            </li>

                            <li className="card-hover d-flex align-items-start mb-3">
                              <i className="fa-solid fa-car-side text-success me-2 mt-1 icon-sm"></i>
                              <span>
                                <strong>Movilidad:</strong> Rentar un auto subcompacto por 30 días
                                cuesta desde $27,000. La cobertura garantiza movilidad continua.
                                <sup>²</sup>
                              </span>
                            </li>

                            <li className="card-hover d-flex align-items-start mb-3">
                              <i className="fa-solid fa-screwdriver-wrench text-success me-2 mt-1 icon-sm"></i>
                              <span>
                                <strong>Reparaciones mayores:</strong> Reparar un motor puede costar entre
                                $68,000 y $119,000.<sup>³</sup>
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="col-md-6">
                          <ul className="text-light text-base list-unstyled">
                            <li className="card-hover d-flex align-items-start mb-3">
                              <i className="fa-solid fa-circle-exclamation text-success me-2 mt-1 icon-sm"></i>
                              <span>
                                <strong>Neumáticos:</strong> Reventar 2 puede costarte al menos $6,800.00.
                                Con cobertura, no pagas nada.
                              </span>
                            </li>

                            <li className="card-hover d-flex align-items-start mb-3">
                              <i className="fa-solid fa-triangle-exclamation text-success me-2 mt-1 icon-sm"></i>
                              <span>
                                <strong>Averías mecánicas:</strong> En promedio se pagan más de $38,000
                                por siniestro. Con garantía, ese monto no saldrá de tu bolsillo.
                              </span>
                            </li>

                            <li className="card-hover d-flex align-items-start mb-3">
                              <i className="fa-solid fa-key text-success me-2 mt-1 icon-sm"></i>
                              <span>
                                <strong>Llaves perdidas:</strong> Reponerlas puede costarte hasta $6,000.00.
                                Con cobertura, no pagas nada.
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <p>
                        <sup>¹</sup> Precio promedio para sedanes como Versa, MG5, Virtus, Aveo, etc.<br />
                        <sup>²</sup> Precio promedio por día de $900.00 para renta de un subcompacto de 4 puertas.<br />
                        <sup>³</sup> Por reparación de vehículos de uso común, el precio de reemplazo de motor puede ser significativamente mayor.
                      </p>
                    </div>

                    <div>
                      <div className="row mt-4">
                        <h5 className="text-warning text-center mb-5">¿Cuánto podrías perder sin una garantía?</h5>
                        <div className="col-md-6">
                          <h6>(%) Siniestralidad de Llantas por Año</h6>
                          <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={llantasData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                              <YAxis unit="%" stroke="var(--axis-stroke)" />
                              <Tooltip formatter={(value) => `${value}%`} />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="siniestralidad"
                                stroke="#dc3545"
                                name="Siniestralidad"
                                label={renderLineLabel}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                          <p className="text-light text-center mt-2 text-base">
                            En promedio, más del 57% de los autos con cobertura de llantas presentan un siniestro cada año.
                          </p>
                        </div>

                        <div className="col-md-6">
                          <h6>Costo Promedio por Siniestro (Llantas)</h6>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={llantasData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                              <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Legend />
                              <Bar
                                dataKey="costo"
                                fill="#28a745"
                                name="Costo Promedio"
                                label={renderBarLabel}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                          <p className="text-light text-center mt-2 text-base">
                            Reparar dos llantas puede costarte más de $3,000. Con garantía, tú pagas $0.
                          </p>
                        </div>

                        <div className="col-md-6 mt-4">
                          <h6>(%) Siniestralidad Cobertura Mecánica</h6>
                          <ResponsiveContainer width="100%" height={300}>
                            <LineChart data={mecanicaData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                              <YAxis unit="%" stroke="var(--axis-stroke)" />
                              <Tooltip formatter={(value) => `${value}%`} />
                              <Legend />
                              <Line
                                type="monotone"
                                dataKey="siniestralidad"
                                stroke="#007bff"
                                name="Siniestralidad"
                                label={renderLineLabel}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                          <p className="text-light text-center mt-2 text-base">
                            La siniestralidad mecánica alcanzó el 79% en 2024. Eso significa que 8 de cada 10 autos necesitaron reparación.
                          </p>
                        </div>

                        <div className="col-md-6 mt-4">
                          <h6>Costo Promedio por Siniestro (Mecánica)</h6>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={mecanicaData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                              <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Legend />
                              <Bar
                                dataKey="costo"
                                fill="#ffc107"
                                name="Costo Promedio"
                                label={renderBarLabel}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                          <p className="text-light text-center mt-2 text-base">
                            El costo promedio de una reparación mecánica en 2024 fue de $38,575. Con garantía, tú no pagas nada.
                          </p>
                        </div>

                        <div className="col-md-6 mt-4">
                          <h6>Evolución del costo de reparaciones</h6>
                          <ResponsiveContainer width="100%" height={350}>
                            <BarChart data={reparacionesData} margin={{ top: 20, right: 30, left: 20, bottom: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                              <XAxis dataKey="year" stroke="var(--axis-stroke)" />
                              <YAxis stroke="var(--axis-stroke)" tickFormatter={formatCurrency} />
                              <Tooltip formatter={(value) => formatCurrency(value)} />
                              <Legend />

                              {/* Barras con colores dinámicos */}
                              <Bar dataKey="costo" name="Costo de Reparaciones" label={renderBarLabel}>
                                {reparacionesData.map((entry, index) => (
                                  <Cell key={`bar-${index}`} fill={entry.color} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                          <p className="text-light text-center mt-2 text-base">
                            En los últimos 3 años, el costo de reparación ha aumentado hasta 2.4 veces. La cobertura evita que estos aumentos afecten tu bolsillo.
                          </p>
                        </div>

                        <div className="col-md-6 mt-4">
                          <h6>Evolución de Siniestralidad</h6>
                          <ResponsiveContainer width="100%" height={350}>
                            <PieChart>
                              <Tooltip formatter={(value) => `${value}%`} />
                              <Legend />
                              <Pie
                                data={siniestralidadData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={120}
                                label={({ value }) => `${value}%`}
                              >
                                {siniestralidadData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                          <p className="text-light text-center mt-2 text-base">
                            La siniestralidad global ha ido en aumento. No es una posibilidad remota: es una realidad cada vez más común.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="alert alert-warning text-center mt-4">
                      <strong>
                        ¿Estás completamente seguro de rechazar la garantía?
                      </strong>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={async () => {
                        await actualizarFirmaModal("10"); // envia 10 para "No, conservar garantía"
                        setGarantiaExtendida("No");
                        setMostrarModalInfo(false);
                      }}
                    >
                      No, conservar garantía
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={async () => {
                        await actualizarFirmaModal("00"); // envia 00 para "Sí, rechazar garantía"
                        setGarantiaExtendida("Si");
                        setMostrarModalInfo(false);
                      }}
                    >
                      Sí, rechazar garantía
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {
        mostrarModalAccesorios && ( //MODAL ACCESORIOS Y GaRANTIAS
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{
              backgroundColor: "rgba(0,0,0,0.3)",
              backdropFilter: "blur(8px)", // Más desenfoque para profundidad
            }}
            onClick={cerrarModalAccesorios}
          >
            <div
              className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
              role="document"
              onClick={(e) => e.stopPropagation()}
              style={{
                animation: "fadeIn 0.3s ease-in-out",
              }}
            >
              <div
                className="modal-content border-0"
                style={{
                  backgroundColor: "#223840",
                  borderRadius: "12px",
                  padding: "20px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                }}
              >
                <div className="modal-header">
                  <h5 className="modal-title text-white">
                    ¡Personaliza tu auto!
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={cerrarModalAccesorios}
                    style={{
                      filter: "brightness(1.2)",
                      transition: "transform 0.2s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.transform = "scale(1.1)")
                    }
                    onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
                  ></button>
                </div>

                <div
                  className="modal-body"
                  style={{ maxHeight: "75vh", overflowY: "auto" }}
                >
                  <div className="sticky-search-bar">
                    {" "}
                    {/* BUSCADOR */}
                    <input
                      type="text"
                      className="form-control search-input"
                      placeholder="Buscar accesorio o garantía..."
                      value={busqueda}
                      onChange={(e) => setBusqueda(e.target.value)}
                    />
                  </div>
                  {/* Garantías */}
                  <div style={{ position: "relative", marginBottom: "35px" }}>
                    {/*<RibbonDiagonal text="Garantias" color="#ffb74d" />*/}
                    <div className="row g-3 mb-4"></div>
                  </div>
                  <div className="row g-3">
                    {garantiasFiltradas.length === 0 ? (
                      <div className="text-white text-center w-100">
                        No se encontraron garantías
                      </div>
                    ) : (
                      garantiasFiltradas.map((food, index) => (
                        <div
                          className="col-xl-3 col-md-4 col-6"
                          key={"garantia-" + index}
                        >
                          <div
                            className={
                              "pos-product card p-2" +
                              (!food.available ? " not-available" : "")
                            }
                            style={{
                              cursor: "pointer",
                              textDecoration: "none",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                              borderRadius: "8px",
                              backgroundColor: "#1f3a47",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.transform = "scale(1.05)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              showAccessoryModal(food);
                            }}
                          >
                            {/* Ícono de interés */}
                            <i
                              className={
                                isInCart(food) ? "fas fa-heart" : "far fa-heart"
                              }
                              title={
                                isInCart(food)
                                  ? "Ya agregado"
                                  : "Agregar a favoritos"
                              }
                              style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                fontSize: "1.2rem",
                                color: "#fff",
                                background:
                                  "linear-gradient(135deg, #ff6f00, #ffb74d)",
                                borderRadius: "50%",
                                padding: "10px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                cursor: "pointer",
                                transition: "transform 0.2s",
                                zIndex: 2,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isInCart(food)) {
                                  addToCartDirectly(food); //agregamos al carrito
                                } else {
                                  Swal.fire(
                                    "Ya agregado",
                                    "Este accesorio ya está en favoritos",
                                    "info"
                                  );
                                }
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform = "scale(1.2)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                            ></i>
                            <div
                              className="img mb-2"
                              style={{ borderRadius: "6px", overflow: "hidden" }}
                            >
                              <img
                                src={food.image}
                                onError={(e) =>
                                (e.target.src =
                                  "/assets/img/product/noimage.png")
                                }
                                alt={food.title}
                                style={{
                                  width: "100%",
                                  height: "180px",
                                  objectFit: "cover",
                                  backgroundColor: "transparent",
                                  borderRadius: "8px",
                                }}
                              />
                            </div>
                            <div className="info text-white">
                              <div className="title fw-bold">{food.title}</div>
                              <div
                                className="desc"
                                style={{ fontSize: "0.9rem", opacity: 0.8 }}
                              >
                                {food.modelo.length > 100
                                  ? `${food.modelo.slice(0, 100)}...`
                                  : food.modelo}
                              </div>
                            </div>
                            {!food.available && (
                              <div className="not-available-text mt-2 text-danger fw-bold">
                                No disponible
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="divider-bar"></div>

                  {/* Accesorios */}
                  <div style={{ position: "relative", marginBottom: "35px" }}>
                    {/*<RibbonDiagonal text="Accesorios" color="#ffb74d" />*/}
                    <div className="row g-3 mb-4">{/* accesorios */}</div>
                  </div>
                  {/* Recorre Accesorios */}
                  <div className="row g-3 mb-4">
                    {accesoriosFiltrados.length === 0 ? (
                      <div className="text-white text-center w-100">
                        No se encontraron accesorios
                      </div>
                    ) : (
                      accesoriosFiltrados.map((food, index) => (
                        <div
                          className="col-xl-3 col-md-4 col-6"
                          key={"accesorio-" + index}
                        >
                          <div
                            className={
                              "pos-product card p-2" +
                              (!food.available ? " not-available" : "")
                            }
                            style={{
                              cursor: "pointer",
                              textDecoration: "none",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                              borderRadius: "8px",
                              backgroundColor: "#1f3a47",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.transform = "scale(1.05)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              showAccessoryModal(food); //solo mostramos la info
                            }}
                          >
                            {/* Ícono de interés */}
                            <i
                              className={
                                isInCart(food) ? "fas fa-heart" : "far fa-heart"
                              }
                              title={
                                isInCart(food)
                                  ? "Ya agregado"
                                  : "Agregar a favoritos"
                              }
                              style={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                fontSize: "1.2rem",
                                color: "#fff",
                                background:
                                  "linear-gradient(135deg, #ff6f00, #ffb74d)",
                                borderRadius: "50%",
                                padding: "10px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                cursor: "pointer",
                                transition: "transform 0.2s",
                                zIndex: 2,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isInCart(food)) {
                                  addToCartDirectly(food); //agregamos al carrito
                                } else {
                                  Swal.fire(
                                    "Ya agregado",
                                    "Este accesorio ya está en favoritos",
                                    "info"
                                  );
                                }
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform = "scale(1.2)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                            ></i>
                            <div
                              className="img mb-2"
                              style={{ borderRadius: "6px", overflow: "hidden" }}
                            >
                              <img
                                src={food.image}
                                onError={(e) =>
                                (e.target.src =
                                  "/assets/img/product/noimage.png")
                                }
                                alt={food.title}
                                style={{
                                  width: "100%",
                                  height: "180px",
                                  objectFit: "cover",
                                  backgroundColor: "transparent", // Fondo transparente
                                  borderRadius: "8px",
                                }}
                              />
                            </div>
                            <div className="info text-white">
                              <div className="title fw-bold">{food.title}</div>
                              <div
                                className="desc"
                                style={{ fontSize: "0.9rem", opacity: 0.8 }}
                              >
                                {food.modelo.length > 100
                                  ? `${food.modelo.slice(0, 100)}...`
                                  : food.modelo}
                              </div>
                            </div>
                            {!food.available && (
                              <div className="not-available-text mt-2 text-danger fw-bold">
                                No disponible
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={cerrarModalAccesorios}
                    style={{
                      padding: "10px 15px",
                      borderRadius: "6px",
                      transition: "background-color 0.3s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.backgroundColor = "#334d5e")
                    }
                    onMouseLeave={(e) => (e.target.style.backgroundColor = "")}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        accessoryModalVisible &&
        accessoryModalData && ( //Modal información accesorios y garantias
          <div
            className="modal modal-pos fade show"
            id="modalPosAccessory"
            style={{
              display: "block",
              backgroundColor: "rgba(0, 0, 0, 0.46)",
              backdropFilter: "blur(3px)",
              zIndex: 1065, // Encima del modal de accesorios
            }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content border-0">
                <div className="modal-pos-product">
                  <div className="modal-pos-product-img d-flex flex-column">
                    {!accessoryModalData.video && (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{
                          height: "250px",
                          backgroundColor: "white",
                          borderRadius: "10px",
                        }}
                      >
                        <img
                          src={accessoryModalData.image}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/assets/img/product/noimage.png";
                          }}
                          alt="Producto"
                          style={{
                            maxHeight: "100%",
                            maxWidth: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    )}
                    {accessoryModalData.video && (
                      <div className="modal-pos-product-video mt-3">
                        <video width="100%" controls>
                          <source
                            src={accessoryModalData.video}
                            type="video/mp4"
                          />
                          Tu navegador no soporta el tag de video.
                        </video>
                      </div>
                    )}
                  </div>

                  <div className="modal-pos-product-info">
                    <div className="fs-4 fw-semibold text-white black-text">
                      {accessoryModalData.title}
                    </div>
                    <div className="text-body text-white mb-2">
                      {accessoryModalData.modelo.length > 200
                        ? `${accessoryModalData.modelo.slice(0, 200)}...`
                        : accessoryModalData.modelo}
                    </div>

                    <hr className="opacity-1" />
                    <div className="row">
                      <div className="col-6">
                        <button
                          className="btn btn-default fw-semibold mb-0 d-block py-3 w-100"
                          onClick={() => setAccessoryModalVisible(false)}
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        mostrarVideoIntro && (
          <div className="video-overlay">
            <video
              src="/assets/vid/garantias/llantas.mp4"
              autoPlay
              onEnded={async () => {
                setMostrarVideoIntro(false);
                setMostrarModalAccesorios(true);
              }}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />

            {/* Botón de omitir */}
            <button
              className="boton-omitir"
              onClick={async () => {
                setMostrarVideoIntro(false);
                setMostrarModalAccesorios(true);
              }}
            >
              Omitir ➤
            </button>
          </div>
        )
      }
      {openFicha && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1040 }}
            onClick={() => setOpenFicha(false)}  /* cerrar al pinchar fuera */
          />

          {/* Modal */}
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {`Ficha Técnica: ${selectedCar.title} ${selectedCar.description}`}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setOpenFicha(false)}
                  />
                </div>

                <div className="modal-body p-0" style={{ height: "80vh" }}>
                  {/* PDF embebido */}
                  {/* Usa iframe, embed u object según tu gusto */}
                  <iframe
                    src={fichaURL}
                    title="Ficha Técnica"
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default PosCustomerOrder;
